import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ArrowLeft, ArrowRight, Globe, MapPin, Phone, Mail, Star, TrendingUp, TrendingDown,
  Lock, Download, AlertCircle, AlertTriangle, Building, Users, Calendar,
  ExternalLink, MessageSquare, Clock, DollarSign, Award, Target, Zap,
  Copy, Check, Briefcase, Cpu, Monitor, FileText,
  Code, Layers, Search, RefreshCw, Brain, Sparkles,
  Trophy, Shield, Minus, CheckCircle, Activity, BarChart3,
  ShieldCheck, ShieldAlert, ShieldOff, TrendingUp as TrendingUpIcon,
  ThumbsUp, ThumbsDown, Info, HelpCircle,
} from 'lucide-react';
import api from '../lib/api';
import { useJobContext } from '../context/JobContext';
import { useAuth } from '../context/AuthContext';
import { copyToClipboard } from '../lib/clipboard';
import { toast } from 'sonner';
import { SocialLink, getSocialsFromLead } from '../components/SocialIcons';

// ─── HELPERS ───
const parseTechStack = (techString) => {
  if (!techString) return {};
  const result = {};
  const pairs = techString.split(';').map(s => s.trim()).filter(Boolean);
  pairs.forEach(pair => {
    const colonIndex = pair.indexOf(':');
    if (colonIndex === -1) return;
    const key = pair.substring(0, colonIndex).trim();
    const value = pair.substring(colonIndex + 1).trim();
    if (key && key !== 'versions' && value) result[key] = value.split(',').map(v => v.trim()).filter(Boolean);
  });
  return result;
};

const parseHours = (hoursFull) => {
  if (!hoursFull) return null;
  const result = {};
  let segments = [];
  if (hoursFull.includes('\uE4CD')) segments = hoursFull.split('\uE4CD').map(s => s.trim()).filter(Boolean);
  else if (hoursFull.includes('')) segments = hoursFull.split('').map(s => s.trim()).filter(Boolean);
  else segments = hoursFull.split(/(?=Monday|Tuesday|Wednesday|Thursday|Friday|Saturday|Sunday)/).map(s => s.trim()).filter(Boolean);
  const days = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];
  days.forEach(day => {
    const found = segments.find(s => s.toLowerCase().startsWith(day.toLowerCase()));
    if (found) {
      let hours = found.replace(new RegExp('^' + day, 'i'), '').trim();
      hours = hours.replace(/^[:\-\s]+/, '').trim().replace(/\s+/g, ' ');
      if (hours) result[day] = hours;
    }
  });
  return Object.keys(result).length > 0 ? result : null;
};

const DAYS = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];

// ─── TECH LOGOS (full) ───
const TECH_LOGOS = {
  // CMS
  wordpress: 'https://cdn.jsdelivr.net/gh/simple-icons/simple-icons/icons/wordpress.svg',
  shopify: 'https://cdn.jsdelivr.net/gh/simple-icons/simple-icons/icons/shopify.svg',
  squarespace: 'https://cdn.jsdelivr.net/gh/simple-icons/simple-icons/icons/squarespace.svg',
  webflow: 'https://cdn.jsdelivr.net/gh/simple-icons/simple-icons/icons/webflow.svg',
  wix: 'https://cdn.jsdelivr.net/gh/simple-icons/simple-icons/icons/wix.svg',
  hubspot_cms: 'https://cdn.jsdelivr.net/gh/simple-icons/simple-icons/icons/hubspot.svg',
  joomla: 'https://cdn.jsdelivr.net/gh/simple-icons/simple-icons/icons/joomla.svg',
  drupal: 'https://cdn.jsdelivr.net/gh/simple-icons/simple-icons/icons/drupal.svg',
  magento: 'https://cdn.jsdelivr.net/gh/simple-icons/simple-icons/icons/magento.svg',
  ghost: 'https://cdn.jsdelivr.net/gh/simple-icons/simple-icons/icons/ghost.svg',
  blogger: 'https://cdn.jsdelivr.net/gh/simple-icons/simple-icons/icons/blogger.svg',
  typo3: 'https://cdn.jsdelivr.net/gh/simple-icons/simple-icons/icons/typo3.svg',
  strapi: 'https://cdn.jsdelivr.net/gh/simple-icons/simple-icons/icons/strapi.svg',
  contentful: 'https://cdn.jsdelivr.net/gh/simple-icons/simple-icons/icons/contentful.svg',
  storyblok: 'https://cdn.jsdelivr.net/gh/simple-icons/simple-icons/icons/storyblok.svg',
  prismic: 'https://cdn.jsdelivr.net/gh/simple-icons/simple-icons/icons/prismic.svg',
  sanity: 'https://cdn.jsdelivr.net/gh/simple-icons/simple-icons/icons/sanity.svg',
  directus: 'https://cdn.jsdelivr.net/gh/simple-icons/simple-icons/icons/directus.svg',
  payloadcms: 'https://cdn.jsdelivr.net/gh/simple-icons/simple-icons/icons/payload.svg',
  keystonejs: 'https://cdn.jsdelivr.net/gh/simple-icons/simple-icons/icons/keystone.svg',
  sitecore: 'https://cdn.jsdelivr.net/gh/simple-icons/simple-icons/icons/sitecore.svg',
  umbraco: 'https://cdn.jsdelivr.net/gh/simple-icons/simple-icons/icons/umbraco.svg',
  kentico: 'https://cdn.jsdelivr.net/gh/simple-icons/simple-icons/icons/kentico.svg',
  contao: 'https://cdn.jsdelivr.net/gh/simple-icons/simple-icons/icons/contao.svg',
  silverstripe: 'https://cdn.jsdelivr.net/gh/simple-icons/simple-icons/icons/silverstripe.svg',
  octobercms: 'https://cdn.jsdelivr.net/gh/simple-icons/simple-icons/icons/octobercms.svg',
  backdrop: 'https://cdn.jsdelivr.net/gh/simple-icons/simple-icons/icons/backdropcms.svg',
  tikiwiki: 'https://cdn.jsdelivr.net/gh/simple-icons/simple-icons/icons/tiki.svg',
  omeka: 'https://cdn.jsdelivr.net/gh/simple-icons/simple-icons/icons/omeka.svg',
  shopware: 'https://cdn.jsdelivr.net/gh/simple-icons/simple-icons/icons/shopware.svg',
  craftcms: 'https://cdn.jsdelivr.net/gh/simple-icons/simple-icons/icons/craftcms.svg',
  processwire: 'https://cdn.jsdelivr.net/gh/simple-icons/simple-icons/icons/processwire.svg',
  statamic: 'https://cdn.jsdelivr.net/gh/simple-icons/simple-icons/icons/statamic.svg',
  kirby: 'https://cdn.jsdelivr.net/gh/simple-icons/simple-icons/icons/kirby.svg',
  pimcore: 'https://cdn.jsdelivr.net/gh/simple-icons/simple-icons/icons/pimcore.svg',
  weebly: 'https://cdn.jsdelivr.net/gh/simple-icons/simple-icons/icons/weebly.svg',
  // JS Frameworks
  react: 'https://cdn.jsdelivr.net/gh/simple-icons/simple-icons/icons/react.svg',
  nextjs: 'https://cdn.jsdelivr.net/gh/simple-icons/simple-icons/icons/nextdotjs.svg',
  vue: 'https://cdn.jsdelivr.net/gh/simple-icons/simple-icons/icons/vuedotjs.svg',
  nuxt: 'https://cdn.jsdelivr.net/gh/simple-icons/simple-icons/icons/nuxtdotjs.svg',
  angular: 'https://cdn.jsdelivr.net/gh/simple-icons/simple-icons/icons/angular.svg',
  svelte: 'https://cdn.jsdelivr.net/gh/simple-icons/simple-icons/icons/svelte.svg',
  sveltekit: 'https://cdn.jsdelivr.net/gh/simple-icons/simple-icons/icons/svelte.svg',
  alpine: 'https://cdn.jsdelivr.net/gh/simple-icons/simple-icons/icons/alpinejs.svg',
  htmx: 'https://cdn.jsdelivr.net/gh/simple-icons/simple-icons/icons/htmx.svg',
  jquery: 'https://cdn.jsdelivr.net/gh/simple-icons/simple-icons/icons/jquery.svg',
  astro: 'https://cdn.jsdelivr.net/gh/simple-icons/simple-icons/icons/astro.svg',
  remix: 'https://cdn.jsdelivr.net/gh/simple-icons/simple-icons/icons/remix.svg',
  gatsby: 'https://cdn.jsdelivr.net/gh/simple-icons/simple-icons/icons/gatsby.svg',
  ember: 'https://cdn.jsdelivr.net/gh/simple-icons/simple-icons/icons/ember.svg',
  backbone: 'https://cdn.jsdelivr.net/gh/simple-icons/simple-icons/icons/backbone.svg',
  solid: 'https://cdn.jsdelivr.net/gh/simple-icons/simple-icons/icons/solid.svg',
  preact: 'https://cdn.jsdelivr.net/gh/simple-icons/simple-icons/icons/preact.svg',
  lit: 'https://cdn.jsdelivr.net/gh/simple-icons/simple-icons/icons/lit.svg',
  stencil: 'https://cdn.jsdelivr.net/gh/simple-icons/simple-icons/icons/stencil.svg',
  qwik: 'https://cdn.jsdelivr.net/gh/simple-icons/simple-icons/icons/qwik.svg',
  fresh: 'https://cdn.jsdelivr.net/gh/simple-icons/simple-icons/icons/fresh.svg',
  meteor: 'https://cdn.jsdelivr.net/gh/simple-icons/simple-icons/icons/meteor.svg',
  mithril: 'https://cdn.jsdelivr.net/gh/simple-icons/simple-icons/icons/mithril.svg',
  phoenix_liveview: 'https://cdn.jsdelivr.net/gh/simple-icons/simple-icons/icons/phoenixframework.svg',
  laravel_livewire: 'https://cdn.jsdelivr.net/gh/simple-icons/simple-icons/icons/livewire.svg',
  hotwire_turbo: 'https://cdn.jsdelivr.net/gh/simple-icons/simple-icons/icons/hotwire.svg',
  stimulus: 'https://cdn.jsdelivr.net/gh/simple-icons/simple-icons/icons/stimulus.svg',
  unpoly: 'https://cdn.jsdelivr.net/gh/simple-icons/simple-icons/icons/unpoly.svg',
  // CSS
  tailwindcss: 'https://cdn.jsdelivr.net/gh/simple-icons/simple-icons/icons/tailwindcss.svg',
  bootstrap: 'https://cdn.jsdelivr.net/gh/simple-icons/simple-icons/icons/bootstrap.svg',
  bulma: 'https://cdn.jsdelivr.net/gh/simple-icons/simple-icons/icons/bulma.svg',
  foundation: 'https://cdn.jsdelivr.net/gh/simple-icons/simple-icons/icons/foundation.svg',
  materialize: 'https://cdn.jsdelivr.net/gh/simple-icons/simple-icons/icons/materialize.svg',
  semantic_ui: 'https://cdn.jsdelivr.net/gh/simple-icons/simple-icons/icons/semanticui.svg',
  pure_css: 'https://cdn.jsdelivr.net/gh/simple-icons/simple-icons/icons/purecss.svg',
  spectre: 'https://cdn.jsdelivr.net/gh/simple-icons/simple-icons/icons/spectre.svg',
  chakra_ui: 'https://cdn.jsdelivr.net/gh/simple-icons/simple-icons/icons/chakraui.svg',
  mui: 'https://cdn.jsdelivr.net/gh/simple-icons/simple-icons/icons/mui.svg',
  ant_design: 'https://cdn.jsdelivr.net/gh/simple-icons/simple-icons/icons/antdesign.svg',
  radix_ui: 'https://cdn.jsdelivr.net/gh/simple-icons/simple-icons/icons/radixui.svg',
  daisyui: 'https://cdn.jsdelivr.net/gh/simple-icons/simple-icons/icons/daisyui.svg',
  flowbite: 'https://cdn.jsdelivr.net/gh/simple-icons/simple-icons/icons/flowbite.svg',
  tachyons: 'https://cdn.jsdelivr.net/gh/simple-icons/simple-icons/icons/tachyons.svg',
  shadcn: 'https://cdn.jsdelivr.net/gh/simple-icons/simple-icons/icons/shadcnui.svg',
  nextui: 'https://cdn.jsdelivr.net/gh/simple-icons/simple-icons/icons/nextui.svg',
  framer_motion: 'https://cdn.jsdelivr.net/gh/simple-icons/simple-icons/icons/framer.svg',
  styled_components: 'https://cdn.jsdelivr.net/gh/simple-icons/simple-icons/icons/styledcomponents.svg',
  emotion: 'https://cdn.jsdelivr.net/gh/simple-icons/simple-icons/icons/emotion.svg',
  sass: 'https://cdn.jsdelivr.net/gh/simple-icons/simple-icons/icons/sass.svg',
  less: 'https://cdn.jsdelivr.net/gh/simple-icons/simple-icons/icons/less.svg',
  // Ecommerce
  woocommerce: 'https://cdn.jsdelivr.net/gh/simple-icons/simple-icons/icons/woocommerce.svg',
  bigcommerce: 'https://cdn.jsdelivr.net/gh/simple-icons/simple-icons/icons/bigcommerce.svg',
  squarespace_commerce: 'https://cdn.jsdelivr.net/gh/simple-icons/simple-icons/icons/squarespace.svg',
  ecwid: 'https://cdn.jsdelivr.net/gh/simple-icons/simple-icons/icons/ecwid.svg',
  opencart: 'https://cdn.jsdelivr.net/gh/simple-icons/simple-icons/icons/opencart.svg',
  shopify_hydrogen: 'https://cdn.jsdelivr.net/gh/simple-icons/simple-icons/icons/shopify.svg',
  prestashop: 'https://cdn.jsdelivr.net/gh/simple-icons/simple-icons/icons/prestashop.svg',
  salesforce_commerce: 'https://cdn.jsdelivr.net/gh/simple-icons/simple-icons/icons/salesforce.svg',
  oracle_commerce: 'https://cdn.jsdelivr.net/gh/simple-icons/simple-icons/icons/oracle.svg',
  sap_hybris: 'https://cdn.jsdelivr.net/gh/simple-icons/simple-icons/icons/sap.svg',
  reaction: 'https://cdn.jsdelivr.net/gh/simple-icons/simple-icons/icons/reaction.svg',
  saleor: 'https://cdn.jsdelivr.net/gh/simple-icons/simple-icons/icons/saleor.svg',
  vendure: 'https://cdn.jsdelivr.net/gh/simple-icons/simple-icons/icons/vendure.svg',
  medusa: 'https://cdn.jsdelivr.net/gh/simple-icons/simple-icons/icons/medusajs.svg',
  sylius: 'https://cdn.jsdelivr.net/gh/simple-icons/simple-icons/icons/sylius.svg',
  spree: 'https://cdn.jsdelivr.net/gh/simple-icons/simple-icons/icons/spree.svg',
  odoo_ecommerce: 'https://cdn.jsdelivr.net/gh/simple-icons/simple-icons/icons/odoo.svg',
  vtex: 'https://cdn.jsdelivr.net/gh/simple-icons/simple-icons/icons/vtex.svg',
  wix_ecommerce: 'https://cdn.jsdelivr.net/gh/simple-icons/simple-icons/icons/wix.svg',
  stripe_checkout: 'https://cdn.jsdelivr.net/gh/simple-icons/simple-icons/icons/stripe.svg',
  shopify_buy_button: 'https://cdn.jsdelivr.net/gh/simple-icons/simple-icons/icons/shopify.svg',
  // Analytics
  google_analytics: 'https://cdn.jsdelivr.net/gh/simple-icons/simple-icons/icons/googleanalytics.svg',
  google_tag_manager: 'https://cdn.jsdelivr.net/gh/simple-icons/simple-icons/icons/googletagmanager.svg',
  facebook_pixel: 'https://cdn.jsdelivr.net/gh/simple-icons/simple-icons/icons/facebook.svg',
  tiktok_pixel: 'https://cdn.jsdelivr.net/gh/simple-icons/simple-icons/icons/tiktok.svg',
  linkedin_insight: 'https://cdn.jsdelivr.net/gh/simple-icons/simple-icons/icons/linkedin.svg',
  twitter_pixel: 'https://cdn.jsdelivr.net/gh/simple-icons/simple-icons/icons/twitter.svg',
  pinterest_tag: 'https://cdn.jsdelivr.net/gh/simple-icons/simple-icons/icons/pinterest.svg',
  snapchat_pixel: 'https://cdn.jsdelivr.net/gh/simple-icons/simple-icons/icons/snapchat.svg',
  reddit_pixel: 'https://cdn.jsdelivr.net/gh/simple-icons/simple-icons/icons/reddit.svg',
  hotjar: 'https://cdn.jsdelivr.net/gh/simple-icons/simple-icons/icons/hotjar.svg',
  clarity: 'https://cdn.jsdelivr.net/gh/simple-icons/simple-icons/icons/clarity.svg',
  mixpanel: 'https://cdn.jsdelivr.net/gh/simple-icons/simple-icons/icons/mixpanel.svg',
  segment: 'https://cdn.jsdelivr.net/gh/simple-icons/simple-icons/icons/segment.svg',
  heap: 'https://cdn.jsdelivr.net/gh/simple-icons/simple-icons/icons/heap.svg',
  fullstory: 'https://cdn.jsdelivr.net/gh/simple-icons/simple-icons/icons/fullstory.svg',
  matomo: 'https://cdn.jsdelivr.net/gh/simple-icons/simple-icons/icons/matomo.svg',
  amplitude: 'https://cdn.jsdelivr.net/gh/simple-icons/simple-icons/icons/amplitude.svg',
  posthog: 'https://cdn.jsdelivr.net/gh/simple-icons/simple-icons/icons/posthog.svg',
  plausible: 'https://cdn.jsdelivr.net/gh/simple-icons/simple-icons/icons/plausibleanalytics.svg',
  fathom: 'https://cdn.jsdelivr.net/gh/simple-icons/simple-icons/icons/fathom.svg',
  simple_analytics: 'https://cdn.jsdelivr.net/gh/simple-icons/simple-icons/icons/simpleanalytics.svg',
  goatcounter: 'https://cdn.jsdelivr.net/gh/simple-icons/simple-icons/icons/goatcounter.svg',
  countly: 'https://cdn.jsdelivr.net/gh/simple-icons/simple-icons/icons/countly.svg',
  rudderstack: 'https://cdn.jsdelivr.net/gh/simple-icons/simple-icons/icons/rudderstack.svg',
  logrocket: 'https://cdn.jsdelivr.net/gh/simple-icons/simple-icons/icons/logrocket.svg',
  smartlook: 'https://cdn.jsdelivr.net/gh/simple-icons/simple-icons/icons/smartlook.svg',
  openreplay: 'https://cdn.jsdelivr.net/gh/simple-icons/simple-icons/icons/openreplay.svg',
  // Hosting
  aws: 'https://cdn.jsdelivr.net/gh/simple-icons/simple-icons/icons/amazonwebservices.svg',
  google_cloud: 'https://cdn.jsdelivr.net/gh/simple-icons/simple-icons/icons/googlecloud.svg',
  azure: 'https://cdn.jsdelivr.net/gh/simple-icons/simple-icons/icons/microsoftazure.svg',
  cloudflare: 'https://cdn.jsdelivr.net/gh/simple-icons/simple-icons/icons/cloudflare.svg',
  vercel: 'https://cdn.jsdelivr.net/gh/simple-icons/simple-icons/icons/vercel.svg',
  netlify: 'https://cdn.jsdelivr.net/gh/simple-icons/simple-icons/icons/netlify.svg',
  heroku: 'https://cdn.jsdelivr.net/gh/simple-icons/simple-icons/icons/heroku.svg',
  firebase: 'https://cdn.jsdelivr.net/gh/simple-icons/simple-icons/icons/firebase.svg',
  digitalocean: 'https://cdn.jsdelivr.net/gh/simple-icons/simple-icons/icons/digitalocean.svg',
  github_pages: 'https://cdn.jsdelivr.net/gh/simple-icons/simple-icons/icons/github.svg',
  gitlab_pages: 'https://cdn.jsdelivr.net/gh/simple-icons/simple-icons/icons/gitlab.svg',
  railway: 'https://cdn.jsdelivr.net/gh/simple-icons/simple-icons/icons/railway.svg',
  render: 'https://cdn.jsdelivr.net/gh/simple-icons/simple-icons/icons/render.svg',
  flyio: 'https://cdn.jsdelivr.net/gh/simple-icons/simple-icons/icons/flyio.svg',
  kinsta: 'https://cdn.jsdelivr.net/gh/simple-icons/simple-icons/icons/kinsta.svg',
  wp_engine: 'https://cdn.jsdelivr.net/gh/simple-icons/simple-icons/icons/wpengine.svg',
  pantheon: 'https://cdn.jsdelivr.net/gh/simple-icons/simple-icons/icons/pantheon.svg',
  platform_sh: 'https://cdn.jsdelivr.net/gh/simple-icons/simple-icons/icons/platformsh.svg',
  kubernetes: 'https://cdn.jsdelivr.net/gh/simple-icons/simple-icons/icons/kubernetes.svg',
  docker: 'https://cdn.jsdelivr.net/gh/simple-icons/simple-icons/icons/docker.svg',
  laravel_forge: 'https://cdn.jsdelivr.net/gh/simple-icons/simple-icons/icons/laravel.svg',
  serverless: 'https://cdn.jsdelivr.net/gh/simple-icons/simple-icons/icons/serverless.svg',
  cloudfoundry: 'https://cdn.jsdelivr.net/gh/simple-icons/simple-icons/icons/cloudfoundry.svg',
  openshift: 'https://cdn.jsdelivr.net/gh/simple-icons/simple-icons/icons/openshift.svg',
  akamai: 'https://cdn.jsdelivr.net/gh/simple-icons/simple-icons/icons/akamai.svg',
  fastly: 'https://cdn.jsdelivr.net/gh/simple-icons/simple-icons/icons/fastly.svg',
  bunnycdn: 'https://cdn.jsdelivr.net/gh/simple-icons/simple-icons/icons/bunny.svg',
  stackpath: 'https://cdn.jsdelivr.net/gh/simple-icons/simple-icons/icons/stackpath.svg',
  // Payments
  stripe: 'https://cdn.jsdelivr.net/gh/simple-icons/simple-icons/icons/stripe.svg',
  paypal: 'https://cdn.jsdelivr.net/gh/simple-icons/simple-icons/icons/paypal.svg',
  square: 'https://cdn.jsdelivr.net/gh/simple-icons/simple-icons/icons/square.svg',
  braintree: 'https://cdn.jsdelivr.net/gh/simple-icons/simple-icons/icons/braintree.svg',
  authorize_net: 'https://cdn.jsdelivr.net/gh/simple-icons/simple-icons/icons/authorizedotnet.svg',
  chargebee: 'https://cdn.jsdelivr.net/gh/simple-icons/simple-icons/icons/chargebee.svg',
  flutterwave: 'https://cdn.jsdelivr.net/gh/simple-icons/simple-icons/icons/flutterwave.svg',
  paystack: 'https://cdn.jsdelivr.net/gh/simple-icons/simple-icons/icons/paystack.svg',
  worldpay: 'https://cdn.jsdelivr.net/gh/simple-icons/simple-icons/icons/worldpay.svg',
  adyen: 'https://cdn.jsdelivr.net/gh/simple-icons/simple-icons/icons/adyen.svg',
  cybersource: 'https://cdn.jsdelivr.net/gh/simple-icons/simple-icons/icons/cybersource.svg',
  klarna: 'https://cdn.jsdelivr.net/gh/simple-icons/simple-icons/icons/klarna.svg',
  afterpay: 'https://cdn.jsdelivr.net/gh/simple-icons/simple-icons/icons/afterpay.svg',
  affirm: 'https://cdn.jsdelivr.net/gh/simple-icons/simple-icons/icons/affirm.svg',
  sezzle: 'https://cdn.jsdelivr.net/gh/simple-icons/simple-icons/icons/sezzle.svg',
  splitit: 'https://cdn.jsdelivr.net/gh/simple-icons/simple-icons/icons/splitit.svg',
  revolut_pay: 'https://cdn.jsdelivr.net/gh/simple-icons/simple-icons/icons/revolut.svg',
  razorpay: 'https://cdn.jsdelivr.net/gh/simple-icons/simple-icons/icons/razorpay.svg',
  instamojo: 'https://cdn.jsdelivr.net/gh/simple-icons/simple-icons/icons/instamojo.svg',
  ccavenue: 'https://cdn.jsdelivr.net/gh/simple-icons/simple-icons/icons/ccavenue.svg',
  khalti: 'https://cdn.jsdelivr.net/gh/simple-icons/simple-icons/icons/khalti.svg',
  iyzico: 'https://cdn.jsdelivr.net/gh/simple-icons/simple-icons/icons/iyzico.svg',
  payu: 'https://cdn.jsdelivr.net/gh/simple-icons/simple-icons/icons/payu.svg',
  mollie: 'https://cdn.jsdelivr.net/gh/simple-icons/simple-icons/icons/mollie.svg',
  clearpay: 'https://cdn.jsdelivr.net/gh/simple-icons/simple-icons/icons/clearpay.svg',
  zip: 'https://cdn.jsdelivr.net/gh/simple-icons/simple-icons/icons/zip.svg',
  // SEO
  yoast: 'https://cdn.jsdelivr.net/gh/simple-icons/simple-icons/icons/yoast.svg',
  rank_math: 'https://cdn.jsdelivr.net/gh/simple-icons/simple-icons/icons/rankmath.svg',
  aioseo: 'https://cdn.jsdelivr.net/gh/simple-icons/simple-icons/icons/aioseo.svg',
  semrush: 'https://cdn.jsdelivr.net/gh/simple-icons/simple-icons/icons/semrush.svg',
  ahrefs: 'https://cdn.jsdelivr.net/gh/simple-icons/simple-icons/icons/ahrefs.svg',
  schema_org: 'https://cdn.jsdelivr.net/gh/simple-icons/simple-icons/icons/schemaorg.svg',
  seo_framework: 'https://cdn.jsdelivr.net/gh/simple-icons/simple-icons/icons/seoframework.svg',
  seopress: 'https://cdn.jsdelivr.net/gh/simple-icons/simple-icons/icons/seopress.svg',
  squirrly: 'https://cdn.jsdelivr.net/gh/simple-icons/simple-icons/icons/squirrly.svg',
  google_search_console: 'https://cdn.jsdelivr.net/gh/simple-icons/simple-icons/icons/googlesearchconsole.svg',
  bing_webmaster: 'https://cdn.jsdelivr.net/gh/simple-icons/simple-icons/icons/microsoftbing.svg',
  yandex_webmaster: 'https://cdn.jsdelivr.net/gh/simple-icons/simple-icons/icons/yandex.svg',
  baidu_webmaster: 'https://cdn.jsdelivr.net/gh/simple-icons/simple-icons/icons/baidu.svg',
  // Security
  sucuri: 'https://cdn.jsdelivr.net/gh/simple-icons/simple-icons/icons/sucuri.svg',
  wordfence: 'https://cdn.jsdelivr.net/gh/simple-icons/simple-icons/icons/wordfence.svg',
  imperva: 'https://cdn.jsdelivr.net/gh/simple-icons/simple-icons/icons/imperva.svg',
  aws_waf: 'https://cdn.jsdelivr.net/gh/simple-icons/simple-icons/icons/amazonwebservices.svg',
  azure_waf: 'https://cdn.jsdelivr.net/gh/simple-icons/simple-icons/icons/microsoftazure.svg',
  google_cloud_armor: 'https://cdn.jsdelivr.net/gh/simple-icons/simple-icons/icons/googlecloud.svg',
  datadome: 'https://cdn.jsdelivr.net/gh/simple-icons/simple-icons/icons/datadome.svg',
  perimeterx: 'https://cdn.jsdelivr.net/gh/simple-icons/simple-icons/icons/perimeterx.svg',
  distil: 'https://cdn.jsdelivr.net/gh/simple-icons/simple-icons/icons/distil.svg',
  reblaze: 'https://cdn.jsdelivr.net/gh/simple-icons/simple-icons/icons/reblaze.svg',
  f5_waf: 'https://cdn.jsdelivr.net/gh/simple-icons/simple-icons/icons/f5.svg',
  modsecurity: 'https://cdn.jsdelivr.net/gh/simple-icons/simple-icons/icons/modsecurity.svg',
  naxsi: 'https://cdn.jsdelivr.net/gh/simple-icons/simple-icons/icons/naxsi.svg',
  openresty_waf: 'https://cdn.jsdelivr.net/gh/simple-icons/simple-icons/icons/openresty.svg',
  // Email
  mailchimp: 'https://cdn.jsdelivr.net/gh/simple-icons/simple-icons/icons/mailchimp.svg',
  klaviyo: 'https://cdn.jsdelivr.net/gh/simple-icons/simple-icons/icons/klaviyo.svg',
  activecampaign: 'https://cdn.jsdelivr.net/gh/simple-icons/simple-icons/icons/activecampaign.svg',
  convertkit: 'https://cdn.jsdelivr.net/gh/simple-icons/simple-icons/icons/convertkit.svg',
  sendinblue: 'https://cdn.jsdelivr.net/gh/simple-icons/simple-icons/icons/sendinblue.svg',
  mailerlite: 'https://cdn.jsdelivr.net/gh/simple-icons/simple-icons/icons/mailerlite.svg',
  drip: 'https://cdn.jsdelivr.net/gh/simple-icons/simple-icons/icons/drip.svg',
  constant_contact: 'https://cdn.jsdelivr.net/gh/simple-icons/simple-icons/icons/constantcontact.svg',
  getresponse: 'https://cdn.jsdelivr.net/gh/simple-icons/simple-icons/icons/getresponse.svg',
  aweber: 'https://cdn.jsdelivr.net/gh/simple-icons/simple-icons/icons/aweber.svg',
  campaign_monitor: 'https://cdn.jsdelivr.net/gh/simple-icons/simple-icons/icons/campaignmonitor.svg',
  omnisend: 'https://cdn.jsdelivr.net/gh/simple-icons/simple-icons/icons/omnisend.svg',
  mailersend: 'https://cdn.jsdelivr.net/gh/simple-icons/simple-icons/icons/mailersend.svg',
  // CRM
  hubspot: 'https://cdn.jsdelivr.net/gh/simple-icons/simple-icons/icons/hubspot.svg',
  salesforce: 'https://cdn.jsdelivr.net/gh/simple-icons/simple-icons/icons/salesforce.svg',
  pipedrive: 'https://cdn.jsdelivr.net/gh/simple-icons/simple-icons/icons/pipedrive.svg',
  zoho: 'https://cdn.jsdelivr.net/gh/simple-icons/simple-icons/icons/zoho.svg',
  keap: 'https://cdn.jsdelivr.net/gh/simple-icons/simple-icons/icons/keap.svg',
  marketo: 'https://cdn.jsdelivr.net/gh/simple-icons/simple-icons/icons/marketo.svg',
  pardot: 'https://cdn.jsdelivr.net/gh/simple-icons/simple-icons/icons/pardot.svg',
  freshsales: 'https://cdn.jsdelivr.net/gh/simple-icons/simple-icons/icons/freshworks.svg',
  copper: 'https://cdn.jsdelivr.net/gh/simple-icons/simple-icons/icons/copper.svg',
  // Chat
  intercom: 'https://cdn.jsdelivr.net/gh/simple-icons/simple-icons/icons/intercom.svg',
  drift: 'https://cdn.jsdelivr.net/gh/simple-icons/simple-icons/icons/drift.svg',
  zendesk: 'https://cdn.jsdelivr.net/gh/simple-icons/simple-icons/icons/zendesk.svg',
  freshchat: 'https://cdn.jsdelivr.net/gh/simple-icons/simple-icons/icons/freshchat.svg',
  livechat: 'https://cdn.jsdelivr.net/gh/simple-icons/simple-icons/icons/livechat.svg',
  olark: 'https://cdn.jsdelivr.net/gh/simple-icons/simple-icons/icons/olark.svg',
  tawk: 'https://cdn.jsdelivr.net/gh/simple-icons/simple-icons/icons/tawk.svg',
  chatbot: 'https://cdn.jsdelivr.net/gh/simple-icons/simple-icons/icons/chatbot.svg',
  crisp: 'https://cdn.jsdelivr.net/gh/simple-icons/simple-icons/icons/crisp.svg',
  smartsupp: 'https://cdn.jsdelivr.net/gh/simple-icons/simple-icons/icons/smartsupp.svg',
  gorgias: 'https://cdn.jsdelivr.net/gh/simple-icons/simple-icons/icons/gorgias.svg',
  kustomer: 'https://cdn.jsdelivr.net/gh/simple-icons/simple-icons/icons/kustomer.svg',
  delightchat: 'https://cdn.jsdelivr.net/gh/simple-icons/simple-icons/icons/delightchat.svg',
  whatsapp_business: 'https://cdn.jsdelivr.net/gh/simple-icons/simple-icons/icons/whatsapp.svg',
  facebook_messenger: 'https://cdn.jsdelivr.net/gh/simple-icons/simple-icons/icons/messenger.svg',
  telegram: 'https://cdn.jsdelivr.net/gh/simple-icons/simple-icons/icons/telegram.svg',
  // Performance
  new_relic: 'https://cdn.jsdelivr.net/gh/simple-icons/simple-icons/icons/newrelic.svg',
  datadog: 'https://cdn.jsdelivr.net/gh/simple-icons/simple-icons/icons/datadog.svg',
  sentry: 'https://cdn.jsdelivr.net/gh/simple-icons/simple-icons/icons/sentry.svg',
  bugsnag: 'https://cdn.jsdelivr.net/gh/simple-icons/simple-icons/icons/bugsnag.svg',
  rollbar: 'https://cdn.jsdelivr.net/gh/simple-icons/simple-icons/icons/rollbar.svg',
  raygun: 'https://cdn.jsdelivr.net/gh/simple-icons/simple-icons/icons/raygun.svg',
  mouseflow: 'https://cdn.jsdelivr.net/gh/simple-icons/simple-icons/icons/mouseflow.svg',
  crazyegg: 'https://cdn.jsdelivr.net/gh/simple-icons/simple-icons/icons/crazyegg.svg',
  lucky_orange: 'https://cdn.jsdelivr.net/gh/simple-icons/simple-icons/icons/luckyorange.svg',
  gtmetrix: 'https://cdn.jsdelivr.net/gh/simple-icons/simple-icons/icons/gtmetrix.svg',
  pingdom: 'https://cdn.jsdelivr.net/gh/simple-icons/simple-icons/icons/pingdom.svg',
  lighthouse: 'https://cdn.jsdelivr.net/gh/simple-icons/simple-icons/icons/lighthouse.svg',
  statuspage: 'https://cdn.jsdelivr.net/gh/simple-icons/simple-icons/icons/statuspage.svg',
  // AI/ML
  openai: 'https://cdn.jsdelivr.net/gh/simple-icons/simple-icons/icons/openai.svg',
  anthropic: 'https://cdn.jsdelivr.net/gh/simple-icons/simple-icons/icons/anthropic.svg',
  google_gemini: 'https://cdn.jsdelivr.net/gh/simple-icons/simple-icons/icons/google.svg',
  huggingface: 'https://cdn.jsdelivr.net/gh/simple-icons/simple-icons/icons/huggingface.svg',
  langchain: 'https://cdn.jsdelivr.net/gh/simple-icons/simple-icons/icons/langchain.svg',
  llamaindex: 'https://cdn.jsdelivr.net/gh/simple-icons/simple-icons/icons/llamaindex.svg',
  pinecone: 'https://cdn.jsdelivr.net/gh/simple-icons/simple-icons/icons/pinecone.svg',
  milvus: 'https://cdn.jsdelivr.net/gh/simple-icons/simple-icons/icons/milvus.svg',
  weaviate: 'https://cdn.jsdelivr.net/gh/simple-icons/simple-icons/icons/weaviate.svg',
  amazon_bedrock: 'https://cdn.jsdelivr.net/gh/simple-icons/simple-icons/icons/amazonwebservices.svg',
  cohere: 'https://cdn.jsdelivr.net/gh/simple-icons/simple-icons/icons/cohere.svg',
  google_nlp: 'https://cdn.jsdelivr.net/gh/simple-icons/simple-icons/icons/google.svg',
  aws_comprehend: 'https://cdn.jsdelivr.net/gh/simple-icons/simple-icons/icons/amazonwebservices.svg',
  aws_rekognition: 'https://cdn.jsdelivr.net/gh/simple-icons/simple-icons/icons/amazonwebservices.svg',
  google_vision: 'https://cdn.jsdelivr.net/gh/simple-icons/simple-icons/icons/google.svg',
  salesforce_einstein: 'https://cdn.jsdelivr.net/gh/simple-icons/simple-icons/icons/salesforce.svg',
  optimizely: 'https://cdn.jsdelivr.net/gh/simple-icons/simple-icons/icons/optimizely.svg',
  vwo: 'https://cdn.jsdelivr.net/gh/simple-icons/simple-icons/icons/vwo.svg',
  // Real estate, job boards, etc.
  zillow: 'https://cdn.jsdelivr.net/gh/simple-icons/simple-icons/icons/zillow.svg',
  realtor: 'https://cdn.jsdelivr.net/gh/simple-icons/simple-icons/icons/realtor.svg',
  redfin: 'https://cdn.jsdelivr.net/gh/simple-icons/simple-icons/icons/redfin.svg',
  trulia: 'https://cdn.jsdelivr.net/gh/simple-icons/simple-icons/icons/trulia.svg',
  apartments: 'https://cdn.jsdelivr.net/gh/simple-icons/simple-icons/icons/apartmentsdotcom.svg',
  rentcafe: 'https://cdn.jsdelivr.net/gh/simple-icons/simple-icons/icons/rentcafe.svg',
  yardi: 'https://cdn.jsdelivr.net/gh/simple-icons/simple-icons/icons/yardi.svg',
  appfolio: 'https://cdn.jsdelivr.net/gh/simple-icons/simple-icons/icons/appfolio.svg',
  buildium: 'https://cdn.jsdelivr.net/gh/simple-icons/simple-icons/icons/buildium.svg',
  propertyware: 'https://cdn.jsdelivr.net/gh/simple-icons/simple-icons/icons/propertyware.svg',
  greenhouse: 'https://cdn.jsdelivr.net/gh/simple-icons/simple-icons/icons/greenhouse.svg',
  lever: 'https://cdn.jsdelivr.net/gh/simple-icons/simple-icons/icons/lever.svg',
  workable: 'https://cdn.jsdelivr.net/gh/simple-icons/simple-icons/icons/workable.svg',
  breezy: 'https://cdn.jsdelivr.net/gh/simple-icons/simple-icons/icons/breezy.svg',
  recruiterbox: 'https://cdn.jsdelivr.net/gh/simple-icons/simple-icons/icons/recruiterbox.svg',
  hire: 'https://cdn.jsdelivr.net/gh/simple-icons/simple-icons/icons/hire.svg',
  indeed: 'https://cdn.jsdelivr.net/gh/simple-icons/simple-icons/icons/indeed.svg',
  linkedin_jobs: 'https://cdn.jsdelivr.net/gh/simple-icons/simple-icons/icons/linkedin.svg',
  ziprecruiter: 'https://cdn.jsdelivr.net/gh/simple-icons/simple-icons/icons/ziprecruiter.svg',
  glassdoor: 'https://cdn.jsdelivr.net/gh/simple-icons/simple-icons/icons/glassdoor.svg',
  jobvite: 'https://cdn.jsdelivr.net/gh/simple-icons/simple-icons/icons/jobvite.svg',
  icims: 'https://cdn.jsdelivr.net/gh/simple-icons/simple-icons/icons/icims.svg',
  // Learning
  moodle: 'https://cdn.jsdelivr.net/gh/simple-icons/simple-icons/icons/moodle.svg',
  thinkific: 'https://cdn.jsdelivr.net/gh/simple-icons/simple-icons/icons/thinkific.svg',
  teachable: 'https://cdn.jsdelivr.net/gh/simple-icons/simple-icons/icons/teachable.svg',
  kajabi: 'https://cdn.jsdelivr.net/gh/simple-icons/simple-icons/icons/kajabi.svg',
  canvas: 'https://cdn.jsdelivr.net/gh/simple-icons/simple-icons/icons/canvas.svg',
  blackboard: 'https://cdn.jsdelivr.net/gh/simple-icons/simple-icons/icons/blackboard.svg',
  brightspace: 'https://cdn.jsdelivr.net/gh/simple-icons/simple-icons/icons/brightspace.svg',
  talentspace: 'https://cdn.jsdelivr.net/gh/simple-icons/simple-icons/icons/talentspace.svg',
  edx: 'https://cdn.jsdelivr.net/gh/simple-icons/simple-icons/icons/edx.svg',
  coursera: 'https://cdn.jsdelivr.net/gh/simple-icons/simple-icons/icons/coursera.svg',
  udemy: 'https://cdn.jsdelivr.net/gh/simple-icons/simple-icons/icons/udemy.svg',
  skillshare: 'https://cdn.jsdelivr.net/gh/simple-icons/simple-icons/icons/skillshare.svg',
  // Membership
  memberpress: 'https://cdn.jsdelivr.net/gh/simple-icons/simple-icons/icons/memberpress.svg',
  restrict_content: 'https://cdn.jsdelivr.net/gh/simple-icons/simple-icons/icons/restrictcontentpro.svg',
  learnworlds: 'https://cdn.jsdelivr.net/gh/simple-icons/simple-icons/icons/learnworlds.svg',
  podia: 'https://cdn.jsdelivr.net/gh/simple-icons/simple-icons/icons/podia.svg',
  gumroad: 'https://cdn.jsdelivr.net/gh/simple-icons/simple-icons/icons/gumroad.svg',
  patreon: 'https://cdn.jsdelivr.net/gh/simple-icons/simple-icons/icons/patreon.svg',
  substack: 'https://cdn.jsdelivr.net/gh/simple-icons/simple-icons/icons/substack.svg',
  circle: 'https://cdn.jsdelivr.net/gh/simple-icons/simple-icons/icons/circle.svg',
  mighty_networks: 'https://cdn.jsdelivr.net/gh/simple-icons/simple-icons/icons/mightynetworks.svg',
  // Forums
  discourse: 'https://cdn.jsdelivr.net/gh/simple-icons/simple-icons/icons/discourse.svg',
  phpbb: 'https://cdn.jsdelivr.net/gh/simple-icons/simple-icons/icons/phpbb.svg',
  vanilla: 'https://cdn.jsdelivr.net/gh/simple-icons/simple-icons/icons/vanillaforums.svg',
  xenforo: 'https://cdn.jsdelivr.net/gh/simple-icons/simple-icons/icons/xenforo.svg',
  flarum: 'https://cdn.jsdelivr.net/gh/simple-icons/simple-icons/icons/flarum.svg',
  mybb: 'https://cdn.jsdelivr.net/gh/simple-icons/simple-icons/icons/mybb.svg',
  vbulletin: 'https://cdn.jsdelivr.net/gh/simple-icons/simple-icons/icons/vbulletin.svg',
  simplemachines: 'https://cdn.jsdelivr.net/gh/simple-icons/simple-icons/icons/simplemachines.svg',
  // Booking
  opentable: 'https://cdn.jsdelivr.net/gh/simple-icons/simple-icons/icons/opentable.svg',
  resy: 'https://cdn.jsdelivr.net/gh/simple-icons/simple-icons/icons/resy.svg',
  calendly: 'https://cdn.jsdelivr.net/gh/simple-icons/simple-icons/icons/calendly.svg',
  acuity: 'https://cdn.jsdelivr.net/gh/simple-icons/simple-icons/icons/acuityscheduling.svg',
  bookly: 'https://cdn.jsdelivr.net/gh/simple-icons/simple-icons/icons/bookly.svg',
  simplybook: 'https://cdn.jsdelivr.net/gh/simple-icons/simple-icons/icons/simplybook.svg',
  booking: 'https://cdn.jsdelivr.net/gh/simple-icons/simple-icons/icons/booking.svg',
  bedful: 'https://cdn.jsdelivr.net/gh/simple-icons/simple-icons/icons/bedful.svg',
  checkfront: 'https://cdn.jsdelivr.net/gh/simple-icons/simple-icons/icons/checkfront.svg',
  rezdy: 'https://cdn.jsdelivr.net/gh/simple-icons/simple-icons/icons/rezdy.svg',
  setmore: 'https://cdn.jsdelivr.net/gh/simple-icons/simple-icons/icons/setmore.svg',
  square_appointments: 'https://cdn.jsdelivr.net/gh/simple-icons/simple-icons/icons/square.svg',
  booker: 'https://cdn.jsdelivr.net/gh/simple-icons/simple-icons/icons/booker.svg',
  mindbody: 'https://cdn.jsdelivr.net/gh/simple-icons/simple-icons/icons/mindbody.svg',
  vagaro: 'https://cdn.jsdelivr.net/gh/simple-icons/simple-icons/icons/vagaro.svg',
  fresha: 'https://cdn.jsdelivr.net/gh/simple-icons/simple-icons/icons/fresha.svg',
  // Events
  eventbrite: 'https://cdn.jsdelivr.net/gh/simple-icons/simple-icons/icons/eventbrite.svg',
  tickettailor: 'https://cdn.jsdelivr.net/gh/simple-icons/simple-icons/icons/tickettailor.svg',
  tito: 'https://cdn.jsdelivr.net/gh/simple-icons/simple-icons/icons/tito.svg',
  billetto: 'https://cdn.jsdelivr.net/gh/simple-icons/simple-icons/icons/billetto.svg',
  // Social
  facebook_login: 'https://cdn.jsdelivr.net/gh/simple-icons/simple-icons/icons/facebook.svg',
  google_login: 'https://cdn.jsdelivr.net/gh/simple-icons/simple-icons/icons/google.svg',
  twitter_login: 'https://cdn.jsdelivr.net/gh/simple-icons/simple-icons/icons/twitter.svg',
  linkedin_login: 'https://cdn.jsdelivr.net/gh/simple-icons/simple-icons/icons/linkedin.svg',
  github_login: 'https://cdn.jsdelivr.net/gh/simple-icons/simple-icons/icons/github.svg',
  apple_login: 'https://cdn.jsdelivr.net/gh/simple-icons/simple-icons/icons/apple.svg',
  social_share: 'https://cdn.jsdelivr.net/gh/simple-icons/simple-icons/icons/sharethis.svg',
  social_embeds: 'https://cdn.jsdelivr.net/gh/simple-icons/simple-icons/icons/embed.svg',
  // Cookie
  cookieyes: 'https://cdn.jsdelivr.net/gh/simple-icons/simple-icons/icons/cookieyes.svg',
  cookiebot: 'https://cdn.jsdelivr.net/gh/simple-icons/simple-icons/icons/cookiebot.svg',
  onetrust: 'https://cdn.jsdelivr.net/gh/simple-icons/simple-icons/icons/onetrust.svg',
  quantcast: 'https://cdn.jsdelivr.net/gh/simple-icons/simple-icons/icons/quantcast.svg',
  cookie_consent: 'https://cdn.jsdelivr.net/gh/simple-icons/simple-icons/icons/cookieconsent.svg',
  gdpr: 'https://cdn.jsdelivr.net/gh/simple-icons/simple-icons/icons/gdpr.svg',
  ccpa: 'https://cdn.jsdelivr.net/gh/simple-icons/simple-icons/icons/ccpa.svg',
  // Accessibility
  accessiware: 'https://cdn.jsdelivr.net/gh/simple-icons/simple-icons/icons/accessiware.svg',
  userway: 'https://cdn.jsdelivr.net/gh/simple-icons/simple-icons/icons/userway.svg',
  accessibe: 'https://cdn.jsdelivr.net/gh/simple-icons/simple-icons/icons/accessibe.svg',
  equality: 'https://cdn.jsdelivr.net/gh/simple-icons/simple-icons/icons/equality.svg',
  audioeye: 'https://cdn.jsdelivr.net/gh/simple-icons/simple-icons/icons/audioeye.svg',
  // Popups
  sumo: 'https://cdn.jsdelivr.net/gh/simple-icons/simple-icons/icons/sumo.svg',
  optinmonster: 'https://cdn.jsdelivr.net/gh/simple-icons/simple-icons/icons/optinmonster.svg',
  convertbox: 'https://cdn.jsdelivr.net/gh/simple-icons/simple-icons/icons/convertbox.svg',
  poptin: 'https://cdn.jsdelivr.net/gh/simple-icons/simple-icons/icons/poptin.svg',
  hello_bar: 'https://cdn.jsdelivr.net/gh/simple-icons/simple-icons/icons/hellobar.svg',
  pushcrew: 'https://cdn.jsdelivr.net/gh/simple-icons/simple-icons/icons/pushcrew.svg',
  onesignal: 'https://cdn.jsdelivr.net/gh/simple-icons/simple-icons/icons/onesignal.svg',
  pushly: 'https://cdn.jsdelivr.net/gh/simple-icons/simple-icons/icons/pushly.svg',
  webpush: 'https://cdn.jsdelivr.net/gh/simple-icons/simple-icons/icons/webpush.svg',
  popup_maker: 'https://cdn.jsdelivr.net/gh/simple-icons/simple-icons/icons/popupmaker.svg',
  // Maps
  google_maps: 'https://cdn.jsdelivr.net/gh/simple-icons/simple-icons/icons/googlemaps.svg',
  mapbox: 'https://cdn.jsdelivr.net/gh/simple-icons/simple-icons/icons/mapbox.svg',
  leaflet: 'https://cdn.jsdelivr.net/gh/simple-icons/simple-icons/icons/leaflet.svg',
  openstreetmap: 'https://cdn.jsdelivr.net/gh/simple-icons/simple-icons/icons/openstreetmap.svg',
  here_maps: 'https://cdn.jsdelivr.net/gh/simple-icons/simple-icons/icons/here.svg',
  bing_maps: 'https://cdn.jsdelivr.net/gh/simple-icons/simple-icons/icons/microsoftbing.svg',
  // Forms
  typeform: 'https://cdn.jsdelivr.net/gh/simple-icons/simple-icons/icons/typeform.svg',
  jotform: 'https://cdn.jsdelivr.net/gh/simple-icons/simple-icons/icons/jotform.svg',
  gravity_forms: 'https://cdn.jsdelivr.net/gh/simple-icons/simple-icons/icons/gravityforms.svg',
  contact_form_7: 'https://cdn.jsdelivr.net/gh/simple-icons/simple-icons/icons/contactform7.svg',
  wpforms: 'https://cdn.jsdelivr.net/gh/simple-icons/simple-icons/icons/wpforms.svg',
  formidable: 'https://cdn.jsdelivr.net/gh/simple-icons/simple-icons/icons/formidable.svg',
  ninja_forms: 'https://cdn.jsdelivr.net/gh/simple-icons/simple-icons/icons/ninjaforms.svg',
  google_forms: 'https://cdn.jsdelivr.net/gh/simple-icons/simple-icons/icons/googleforms.svg',
  survey_monkey: 'https://cdn.jsdelivr.net/gh/simple-icons/simple-icons/icons/surveymonkey.svg',
  qualaroo: 'https://cdn.jsdelivr.net/gh/simple-icons/simple-icons/icons/qualaroo.svg',
  usabilla: 'https://cdn.jsdelivr.net/gh/simple-icons/simple-icons/icons/usabilla.svg',
  survicate: 'https://cdn.jsdelivr.net/gh/simple-icons/simple-icons/icons/survicate.svg',
  // Video
  youtube: 'https://cdn.jsdelivr.net/gh/simple-icons/simple-icons/icons/youtube.svg',
  vimeo: 'https://cdn.jsdelivr.net/gh/simple-icons/simple-icons/icons/vimeo.svg',
  wistia: 'https://cdn.jsdelivr.net/gh/simple-icons/simple-icons/icons/wistia.svg',
  vidyard: 'https://cdn.jsdelivr.net/gh/simple-icons/simple-icons/icons/vidyard.svg',
  brightcove: 'https://cdn.jsdelivr.net/gh/simple-icons/simple-icons/icons/brightcove.svg',
  jwplayer: 'https://cdn.jsdelivr.net/gh/simple-icons/simple-icons/icons/jwplayer.svg',
  dailymotion: 'https://cdn.jsdelivr.net/gh/simple-icons/simple-icons/icons/dailymotion.svg',
  // Audio
  soundcloud: 'https://cdn.jsdelivr.net/gh/simple-icons/simple-icons/icons/soundcloud.svg',
  spotify: 'https://cdn.jsdelivr.net/gh/simple-icons/simple-icons/icons/spotify.svg',
  apple_music: 'https://cdn.jsdelivr.net/gh/simple-icons/simple-icons/icons/applemusic.svg',
  audioboom: 'https://cdn.jsdelivr.net/gh/simple-icons/simple-icons/icons/audioboom.svg',
  mixcloud: 'https://cdn.jsdelivr.net/gh/simple-icons/simple-icons/icons/mixcloud.svg',
  podbean: 'https://cdn.jsdelivr.net/gh/simple-icons/simple-icons/icons/podbean.svg',
  buzzsprout: 'https://cdn.jsdelivr.net/gh/simple-icons/simple-icons/icons/buzzsprout.svg',
  anchor: 'https://cdn.jsdelivr.net/gh/simple-icons/simple-icons/icons/anchor.svg',
  transistor: 'https://cdn.jsdelivr.net/gh/simple-icons/simple-icons/icons/transistor.svg',
  simplecast: 'https://cdn.jsdelivr.net/gh/simple-icons/simple-icons/icons/simplecast.svg',
  // Live streaming
  twitch: 'https://cdn.jsdelivr.net/gh/simple-icons/simple-icons/icons/twitch.svg',
  periscope: 'https://cdn.jsdelivr.net/gh/simple-icons/simple-icons/icons/periscope.svg',
  ustream: 'https://cdn.jsdelivr.net/gh/simple-icons/simple-icons/icons/ustream.svg',
  streamyard: 'https://cdn.jsdelivr.net/gh/simple-icons/simple-icons/icons/streamyard.svg',
  restream: 'https://cdn.jsdelivr.net/gh/simple-icons/simple-icons/icons/restream.svg',
  // PDF
  wkhtmltopdf: 'https://cdn.jsdelivr.net/gh/simple-icons/simple-icons/icons/wkhtmltopdf.svg',
  pdfjs: 'https://cdn.jsdelivr.net/gh/simple-icons/simple-icons/icons/pdf.svg',
  jspdf: 'https://cdn.jsdelivr.net/gh/simple-icons/simple-icons/icons/jspdf.svg',
  react_pdf: 'https://cdn.jsdelivr.net/gh/simple-icons/simple-icons/icons/reactpdf.svg',
  puppeteer_pdf: 'https://cdn.jsdelivr.net/gh/simple-icons/simple-icons/icons/puppeteer.svg',
  mpdf: 'https://cdn.jsdelivr.net/gh/simple-icons/simple-icons/icons/mpdf.svg',
  dompdf: 'https://cdn.jsdelivr.net/gh/simple-icons/simple-icons/icons/dompdf.svg',
  html2pdf: 'https://cdn.jsdelivr.net/gh/simple-icons/simple-icons/icons/html2pdf.svg',
  // Mobile
  react_native: 'https://cdn.jsdelivr.net/gh/simple-icons/simple-icons/icons/react.svg',
  flutter: 'https://cdn.jsdelivr.net/gh/simple-icons/simple-icons/icons/flutter.svg',
  cordova: 'https://cdn.jsdelivr.net/gh/simple-icons/simple-icons/icons/cordova.svg',
  ionic: 'https://cdn.jsdelivr.net/gh/simple-icons/simple-icons/icons/ionic.svg',
  xamarin: 'https://cdn.jsdelivr.net/gh/simple-icons/simple-icons/icons/xamarin.svg',
  unity: 'https://cdn.jsdelivr.net/gh/simple-icons/simple-icons/icons/unity.svg',
  capacitor: 'https://cdn.jsdelivr.net/gh/simple-icons/simple-icons/icons/capacitor.svg',
  nativescript: 'https://cdn.jsdelivr.net/gh/simple-icons/simple-icons/icons/nativescript.svg',
  // WebAssembly
  wasm: 'https://cdn.jsdelivr.net/gh/simple-icons/simple-icons/icons/webassembly.svg',
  emscripten: 'https://cdn.jsdelivr.net/gh/simple-icons/simple-icons/icons/emscripten.svg',
  blazor: 'https://cdn.jsdelivr.net/gh/simple-icons/simple-icons/icons/blazor.svg',
  assemblyscript: 'https://cdn.jsdelivr.net/gh/simple-icons/simple-icons/icons/assemblyscript.svg',
  wasmer: 'https://cdn.jsdelivr.net/gh/simple-icons/simple-icons/icons/wasmer.svg',
  // GraphQL
  apollo: 'https://cdn.jsdelivr.net/gh/simple-icons/simple-icons/icons/apollographql.svg',
  relay: 'https://cdn.jsdelivr.net/gh/simple-icons/simple-icons/icons/relay.svg',
  graphql_js: 'https://cdn.jsdelivr.net/gh/simple-icons/simple-icons/icons/graphql.svg',
  graphql_playground: 'https://cdn.jsdelivr.net/gh/simple-icons/simple-icons/icons/graphql.svg',
  graphiql: 'https://cdn.jsdelivr.net/gh/simple-icons/simple-icons/icons/graphql.svg',
  hasura: 'https://cdn.jsdelivr.net/gh/simple-icons/simple-icons/icons/hasura.svg',
  // REST
  swagger: 'https://cdn.jsdelivr.net/gh/simple-icons/simple-icons/icons/swagger.svg',
  openapi: 'https://cdn.jsdelivr.net/gh/simple-icons/simple-icons/icons/openapi.svg',
  postman: 'https://cdn.jsdelivr.net/gh/simple-icons/simple-icons/icons/postman.svg',
  rest_framework: 'https://cdn.jsdelivr.net/gh/simple-icons/simple-icons/icons/djangorest.svg',
  api_platform: 'https://cdn.jsdelivr.net/gh/simple-icons/simple-icons/icons/apiplatform.svg',
  // WebSockets
  socketio: 'https://cdn.jsdelivr.net/gh/simple-icons/simple-icons/icons/socketio.svg',
  websocket: 'https://cdn.jsdelivr.net/gh/simple-icons/simple-icons/icons/websocket.svg',
  pusher: 'https://cdn.jsdelivr.net/gh/simple-icons/simple-icons/icons/pusher.svg',
  ably: 'https://cdn.jsdelivr.net/gh/simple-icons/simple-icons/icons/ably.svg',
  realtime: 'https://cdn.jsdelivr.net/gh/simple-icons/simple-icons/icons/realtime.svg',
  // RPC
  grpc: 'https://cdn.jsdelivr.net/gh/simple-icons/simple-icons/icons/grpc.svg',
  json_rpc: 'https://cdn.jsdelivr.net/gh/simple-icons/simple-icons/icons/json.svg',
  xml_rpc: 'https://cdn.jsdelivr.net/gh/simple-icons/simple-icons/icons/xml.svg',
  soap: 'https://cdn.jsdelivr.net/gh/simple-icons/simple-icons/icons/soap.svg',
  // Feedback
  usersnap: 'https://cdn.jsdelivr.net/gh/simple-icons/simple-icons/icons/usersnap.svg',
  userfeel: 'https://cdn.jsdelivr.net/gh/simple-icons/simple-icons/icons/userfeel.svg',
  maze: 'https://cdn.jsdelivr.net/gh/simple-icons/simple-icons/icons/maze.svg',
  usertesting: 'https://cdn.jsdelivr.net/gh/simple-icons/simple-icons/icons/usertesting.svg',
  // Accounting
  quickbooks: 'https://cdn.jsdelivr.net/gh/simple-icons/simple-icons/icons/quickbooks.svg',
  xero: 'https://cdn.jsdelivr.net/gh/simple-icons/simple-icons/icons/xero.svg',
  freshbooks: 'https://cdn.jsdelivr.net/gh/simple-icons/simple-icons/icons/freshbooks.svg',
  wave: 'https://cdn.jsdelivr.net/gh/simple-icons/simple-icons/icons/wave.svg',
  zoho_books: 'https://cdn.jsdelivr.net/gh/simple-icons/simple-icons/icons/zoho.svg',
  sage: 'https://cdn.jsdelivr.net/gh/simple-icons/simple-icons/icons/sage.svg',
  netsuite: 'https://cdn.jsdelivr.net/gh/simple-icons/simple-icons/icons/netsuite.svg',
  // Languages
  nodejs: 'https://cdn.jsdelivr.net/gh/simple-icons/simple-icons/icons/nodedotjs.svg',
  php: 'https://cdn.jsdelivr.net/gh/simple-icons/simple-icons/icons/php.svg',
  laravel: 'https://cdn.jsdelivr.net/gh/simple-icons/simple-icons/icons/laravel.svg',
  python: 'https://cdn.jsdelivr.net/gh/simple-icons/simple-icons/icons/python.svg',
  ruby: 'https://cdn.jsdelivr.net/gh/simple-icons/simple-icons/icons/ruby.svg',
  go: 'https://cdn.jsdelivr.net/gh/simple-icons/simple-icons/icons/go.svg',
  rust: 'https://cdn.jsdelivr.net/gh/simple-icons/simple-icons/icons/rust.svg',
  typescript: 'https://cdn.jsdelivr.net/gh/simple-icons/simple-icons/icons/typescript.svg',
  vite: 'https://cdn.jsdelivr.net/gh/simple-icons/simple-icons/icons/vite.svg',
  // Misc
  google_fonts: 'https://cdn.jsdelivr.net/gh/simple-icons/simple-icons/icons/googlefonts.svg',
  fontawesome: 'https://cdn.jsdelivr.net/gh/simple-icons/simple-icons/icons/fontawesome.svg',
  cloudinary: 'https://cdn.jsdelivr.net/gh/simple-icons/simple-icons/icons/cloudinary.svg',
  algolia: 'https://cdn.jsdelivr.net/gh/simple-icons/simple-icons/icons/algolia.svg',
  mapbox: 'https://cdn.jsdelivr.net/gh/simple-icons/simple-icons/icons/mapbox.svg',
  leaflet: 'https://cdn.jsdelivr.net/gh/simple-icons/simple-icons/icons/leaflet.svg',
  momentjs: 'https://cdn.jsdelivr.net/gh/simple-icons/simple-icons/icons/moment.svg',
  lodash: 'https://cdn.jsdelivr.net/gh/simple-icons/simple-icons/icons/lodash.svg',
  swiper: 'https://cdn.jsdelivr.net/gh/simple-icons/simple-icons/icons/swiper.svg',
  gsap: 'https://cdn.jsdelivr.net/gh/simple-icons/simple-icons/icons/gsap.svg',
  threejs: 'https://cdn.jsdelivr.net/gh/simple-icons/simple-icons/icons/threedotjs.svg',
  chartjs: 'https://cdn.jsdelivr.net/gh/simple-icons/simple-icons/icons/chartdotjs.svg',
  d3: 'https://cdn.jsdelivr.net/gh/simple-icons/simple-icons/icons/d3dotjs.svg',
  echarts: 'https://cdn.jsdelivr.net/gh/simple-icons/simple-icons/icons/echarts.svg',
  highlightjs: 'https://cdn.jsdelivr.net/gh/simple-icons/simple-icons/icons/highlightjs.svg',
  prismjs: 'https://cdn.jsdelivr.net/gh/simple-icons/simple-icons/icons/prism.svg',
  typedjs: 'https://cdn.jsdelivr.net/gh/simple-icons/simple-icons/icons/typed.svg',
  particles: 'https://cdn.jsdelivr.net/gh/simple-icons/simple-icons/icons/particlesdotjs.svg',
  animejs: 'https://cdn.jsdelivr.net/gh/simple-icons/simple-icons/icons/anime.svg',
  scrollmagic: 'https://cdn.jsdelivr.net/gh/simple-icons/simple-icons/icons/scrollmagic.svg',
};

// ─── TECH LABELS (full) ───
const TECH_LABELS = {
  cms: { label: 'CMS', color: '#e94560' },
  analytics: { label: 'Analytics', color: '#4ecca3' },
  javascript_frameworks: { label: 'JS Frameworks', color: '#f57f17' },
  css_frameworks: { label: 'CSS Frameworks', color: '#2196f3' },
  seo_tools: { label: 'SEO Tools', color: '#7c3aed' },
  web_servers: { label: 'Web Servers', color: '#ff6f00' },
  hosting: { label: 'Hosting', color: '#00bcd4' },
  payment_gateways: { label: 'Payments', color: '#9c27b0' },
  ecommerce: { label: 'Ecommerce', color: '#e91e63' },
  security: { label: 'Security', color: '#ef4444' },
  email_marketing: { label: 'Email Marketing', color: '#8b5cf6' },
  chat_support: { label: 'Chat & Support', color: '#06b6d4' },
  crm: { label: 'CRM', color: '#f97316' },
  scheduling: { label: 'Scheduling', color: '#22c55e' },
  performance_monitoring: { label: 'Performance', color: '#3b82f6' },
  ai_ml: { label: 'AI & ML', color: '#a855f7' },
  cookie_consent: { label: 'Cookie Consent', color: '#64748b' },
  accessibility: { label: 'Accessibility', color: '#14b8a6' },
  forms_surveys: { label: 'Forms & Surveys', color: '#ec4899' },
  video_hosting: { label: 'Video', color: '#ef4444' },
  audio_hosting: { label: 'Audio', color: '#84cc16' },
  booking_systems: { label: 'Booking', color: '#06b6d4' },
  social_integration: { label: 'Social', color: '#2563eb' },
  maps_location: { label: 'Maps', color: '#10b981' },
  popups_notifications: { label: 'Popups', color: '#f59e0b' },
  recruitment_hr: { label: 'HR & Recruiting', color: '#6366f1' },
  accounting: { label: 'Accounting', color: '#0891b2' },
  learning_lms: { label: 'Learning/LMS', color: '#7c3aed' },
  membership: { label: 'Membership', color: '#db2777' },
  forums: { label: 'Forums', color: '#ea580c' },
  event_management: { label: 'Events', color: '#2563eb' },
  live_streaming: { label: 'Live Streaming', color: '#dc2626' },
  mobile_sdks: { label: 'Mobile SDKs', color: '#65a30d' },
  graphql: { label: 'GraphQL', color: '#ec4899' },
  rest_api: { label: 'REST API', color: '#0891b2' },
  websockets: { label: 'WebSockets', color: '#d946ef' },
  webassembly: { label: 'WebAssembly', color: '#9333ea' },
  feedback_tools: { label: 'Feedback', color: '#0d9488' },
  print_pdf: { label: 'PDF/Print', color: '#78716c' },
  real_estate: { label: 'Real Estate', color: '#0369a1' },
  job_boards: { label: 'Job Boards', color: '#b45309' },
  rpc: { label: 'RPC', color: '#4b5563' },
  misc: { label: 'Other', color: '#6b7280' },
};

// ─── LOADING DOTS ───
const LoadingDots = () => (
  <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5 }}>
    <span style={{ width: 7, height: 7, borderRadius: '50%', background: 'currentColor', animation: 'loadingDot 1.4s ease-in-out 0s infinite' }} />
    <span style={{ width: 7, height: 7, borderRadius: '50%', background: 'currentColor', animation: 'loadingDot 1.4s ease-in-out 0.2s infinite' }} />
    <span style={{ width: 7, height: 7, borderRadius: '50%', background: 'currentColor', animation: 'loadingDot 1.4s ease-in-out 0.4s infinite' }} />
  </span>
);

// ─── MAIN COMPONENT ───
export default function CompanyProfile({ lead, onBack }) {
  const [copied, setCopied] = useState(false);
  const [exportingSheets, setExportingSheets] = useState(false);
  const [exportingPdf, setExportingPdf] = useState(false);
  const [reEnriching, setReEnriching] = useState(false);
  const [reScoring, setReScoring] = useState(false);
  const [reProcessing, setReProcessing] = useState(false);
  const [activePitchTab, setActivePitchTab] = useState(0);
  const [currentLead, setCurrentLead] = useState(lead);
  const [plan, setPlan] = useState('free');
  const { user } = useAuth();
  const [teamMembers, setTeamMembers] = useState([]);
  const [newOwnerId, setNewOwnerId] = useState('');
  const [reassigning, setReassigning] = useState(false);
  const [role, setRole] = useState('user');
  const [reviewData, setReviewData] = useState(null);
  const [statusHistory, setStatusHistory] = useState([]);
  const timeoutRef = useRef(null);
  const navigate = useNavigate();

  const { state: jobState } = useJobContext();
  const isProcessing = reEnriching || reScoring || reProcessing;

  useEffect(() => { setCurrentLead(lead); }, [lead]);

  // On mount: restore processing state from sessionStorage (survives refresh)
  useEffect(() => {
    if (!currentLead?.company_name) return;
    const stored = sessionStorage.getItem('ileadx_processing_' + currentLead.company_name);
    if (!stored) return;
    try {
      const data = JSON.parse(stored);
      // Only restore if started within last 10 minutes
      if (Date.now() - data.started < 600000) {
        api.get('/api/leads/pending-operations').then(res => {
          const ops = res.data?.operations || [];
          const stillProcessing = ops.some(op => op.company_name === currentLead.company_name && op.status === 'processing');
          if (stillProcessing) {
            processingRef.current = true;
            setReProcessing(true);
          } else {
            sessionStorage.removeItem('ileadx_processing_' + currentLead.company_name);
          }
        }).catch(() => {});
      } else {
        sessionStorage.removeItem('ileadx_processing_' + currentLead.company_name);
      }
    } catch {}
  }, [currentLead?.company_name]);

  useEffect(() => {
    api.get('/api/auth/me').then(r => { setPlan(r.data.plan || 'free'); setRole(r.data.role || 'user'); }).catch(() => {});
  }, []);
  useEffect(() => { return () => { if (timeoutRef.current) clearTimeout(timeoutRef.current); }; }, []);

  // Load team members for reassignment if owner/admin
  useEffect(() => {
    if (!user?.organization_id || !['owner','admin'].includes(user?.team_role)) return;
    const fetchMembers = async () => {
      try {
        const res = await api.get('/api/organization/members');
        setTeamMembers(res.data.members || []);
      } catch {}
    };
    fetchMembers();
  }, [user?.organization_id, user?.team_role]);

  useEffect(() => {
    if (currentLead?.raw_reviews) {
      try {
        const data = typeof currentLead.raw_reviews === 'string' ? JSON.parse(currentLead.raw_reviews) : currentLead.raw_reviews;
        setReviewData(data);
      } catch { setReviewData(null); }
    }
  }, [currentLead]);

  // Load status history
  useEffect(() => {
    if (!currentLead?.company_name) return;
    api.get('/api/leads/status-history', { params: { company_name: currentLead.company_name } })
      .then(res => setStatusHistory(res.data.history || []))
      .catch(() => {});
  }, [currentLead?.company_name]);

  const processingRef = useRef(false);

  const fetchLeadData = async (companyName) => {
    try {
      const res = await api.get(`/api/leads/detail?company_name=${encodeURIComponent(companyName)}`);
      if (res.data?.lead) {
        setCurrentLead(res.data.lead);
        return res.data.lead;
      }
    } catch {}
    return null;
  };

  // Listen for lead updates from unified JobContext WebSocket
  useEffect(() => {
    if (!currentLead?.company_name) return;
    const leadOp = jobState.leadOps[currentLead.company_name];
    if (leadOp && leadOp.status !== 'processing' && (reEnriching || reScoring || reProcessing)) {
      // Operation completed — fetch fresh data and clear loading
      fetchLeadData(currentLead.company_name);
      setReEnriching(false);
      setReScoring(false);
      setReProcessing(false);
      try { sessionStorage.removeItem('ileadx_processing_' + currentLead.company_name); } catch {}
      if (leadOp.status === 'completed') {
        toast.success('Operation completed!');
      }
    }
  }, [jobState.leadOps, currentLead?.company_name]);

  // Safety timeout for operations
  useEffect(() => {
    if (!isProcessing) return;
    const timeout = setTimeout(() => {
      if (reEnriching || reScoring || reProcessing) {
        setReEnriching(false);
        setReScoring(false);
        setReProcessing(false);
        fetchLeadData(currentLead?.company_name);
        toast('Operation timed out. Data may be partial.', { description: 'Try refreshing.' });
      }
    }, 600000); // 10 minutes
    return () => clearTimeout(timeout);
  }, [isProcessing, currentLead?.company_name]);

  // ─── DERIVED DATA ───
  const score = currentLead.lead_score;
  const urgency = currentLead.urgency_score;
  const scoreColor = score >= 70 ? 'var(--success)' : score >= 40 ? 'var(--warning)' : score > 0 ? 'var(--info)' : 'var(--text-tertiary)';
  const scoreBg = score >= 70 ? 'var(--success-bg)' : score >= 40 ? 'var(--warning-bg)' : score > 0 ? 'var(--info-bg)' : 'var(--bg-tertiary)';
  const scoreLabel = score >= 70 ? 'Hot Lead' : score >= 40 ? 'Warm Lead' : score > 0 ? 'Cool Lead' : 'Unscored';
  
  // V4: Score Breakdown
  let scoreBreakdownRaw = currentLead.score_breakdown || {};
  let scoreBreakdown = scoreBreakdownRaw;
  if (typeof scoreBreakdown === 'string') {
    try { scoreBreakdown = JSON.parse(scoreBreakdown); } catch { scoreBreakdown = {}; }
  }
  if (typeof scoreBreakdown !== 'object' || Array.isArray(scoreBreakdown)) scoreBreakdown = {};
  const businessQuality = currentLead.business_quality || scoreBreakdown.business_quality || 0;
  const serviceFit = currentLead.service_fit || scoreBreakdown.service_fit || 0;
  let scmRaw = currentLead.service_complaint_mapping || {};
  let serviceComplaintMapping = scmRaw;
  if (typeof serviceComplaintMapping === 'string') {
    try { serviceComplaintMapping = JSON.parse(serviceComplaintMapping); } catch { serviceComplaintMapping = {}; }
  }
  if (typeof serviceComplaintMapping !== 'object' || Array.isArray(serviceComplaintMapping)) serviceComplaintMapping = {};
  const honestAssessment = currentLead.honest_assessment || '';
  const triggerEventsRaw = currentLead.trigger_events_to_watch || [];
  let triggerEvents = triggerEventsRaw;
  if (typeof triggerEvents === 'string') {
    try { triggerEvents = JSON.parse(triggerEvents); } catch {}
  }
  if (!Array.isArray(triggerEvents)) triggerEvents = [];
  const hasV4Data = businessQuality > 0 || serviceFit > 0 || Object.keys(scoreBreakdown).length > 0;
  const urgencyColor = urgency >= 70 ? 'var(--error)' : urgency >= 40 ? 'var(--warning)' : urgency > 0 ? 'var(--text-secondary)' : 'var(--text-tertiary)';
  const urgencyBg = urgency >= 70 ? 'var(--error-bg)' : urgency >= 40 ? 'var(--warning-bg)' : urgency > 0 ? 'var(--bg-tertiary)' : 'var(--bg-tertiary)';
  const urgencyLabel = urgency >= 70 ? 'Act Now' : urgency >= 40 ? 'This Week' : urgency > 0 ? 'Pipeline' : '';

  const pitchAngles = (() => {
    if (!currentLead.pitch_angle) return [];
    const labels = ['Empathy', 'ROI', 'Urgency', 'Data-Driven', 'Direct'];
    try {
      const parsed = JSON.parse(currentLead.pitch_angle);
      if (Array.isArray(parsed)) return parsed.slice(0, plan === 'enterprise' ? 5 : plan === 'pro' ? 3 : 1).map((text, i) => {
        const cleaned = text.replace(/^\[?(Empathy|ROI|Urgency|Data-Driven|Direct|Data)(\s*angle)?\s*[—–-]\s*/i, '').replace(/\]$/, '').trim();
        return { type: labels[i] || 'Pitch', label: labels[i] || 'Pitch', text: cleaned || text };
      });
    } catch {}
    return [{ type: 'Primary', label: 'Pitch Angle', text: currentLead.pitch_angle }];
  })();

  const initials = (currentLead.company_name || '?').split(' ').map(w => w[0]).join('').toUpperCase().slice(0, 2);
  const socials = getSocialsFromLead(currentLead);
  const techStack = parseTechStack(currentLead.tech_stack);
  const hoursData = parseHours(currentLead.hours_full);
  const reviewDist = (() => {
    if (reviewData?.distribution) return reviewData.distribution;
    if (!currentLead.google_review_count || !currentLead.sentiment_positive_pct) return null;
    const total = currentLead.google_review_count;
    const pos = Math.round((currentLead.sentiment_positive_pct / 100) * total);
    const neu = Math.round((currentLead.sentiment_neutral_pct / 100) * total);
    const neg = Math.round((currentLead.sentiment_negative_pct / 100) * total);
    const fiveStar = Math.round(pos * 0.7);
    const fourStar = pos - fiveStar;
    const twoStar = Math.round(neg * 0.6);
    const oneStar = neg - twoStar;
    return { '5': fiveStar, '4': fourStar, '3': neu, '2': twoStar, '1': oneStar };
  })();
  const maxReview = reviewDist ? Math.max(...Object.values(reviewDist), 1) : 1;

  // ─── ACTIONS ───
  const copyPitch = () => {
    navigator.clipboard.writeText(currentLead.pitch_angle || '');
    setCopied(true);
    toast.success('Pitch copied! Ready to paste into your outreach.');
    setTimeout(() => setCopied(false), 2000);
  };

  const doEnrich = async () => {
    setReEnriching(true);
    try { sessionStorage.setItem('ileadx_processing_' + currentLead.company_name, JSON.stringify({ company: currentLead.company_name, started: Date.now() })); } catch {}
    try {
      const res = await api.post('/api/leads/regenerate-score', { company_name: currentLead.company_name, search_query: currentLead.search_query, service_context: currentLead.service_context || 'B2B software', enrich_only: true });
      if (res.data?.status === 'already_processing') {
        setReEnriching(false);
        toast('Already processing this lead.', { description: 'A job is already running. Please wait for it to complete.', duration: 5000 });
        return;
      }
      toast('Job queued — processing on workers');
    } catch (err) {
      setReEnriching(false);
      if (err.response?.status === 429) toast(err.response?.data?.detail || 'Quota reached.', { description: 'Upgrade your plan to continue.', duration: 6000, action: { label: 'View Plans', onClick: () => window.location.href = '/dashboard/billing?tab=plans' } });
      else toast('Something went wrong.', { description: 'Please try again in a moment.', duration: 5000 });
    }
  };

  const doScore = async () => {
    setReScoring(true);
    try { sessionStorage.setItem('ileadx_processing_' + currentLead.company_name, JSON.stringify({ company: currentLead.company_name, started: Date.now() })); } catch {}
    try {
      const res = await api.post('/api/leads/regenerate-score', { company_name: currentLead.company_name, search_query: currentLead.search_query, service_context: currentLead.service_context || 'B2B software', enrich_only: false });
      if (res.data?.status === 'already_processing') {
        setReScoring(false);
        toast('Already processing this lead.', { description: 'A job is already running. Please wait for it to complete.', duration: 5000 });
        return;
      }
    } catch (err) {
      setReScoring(false);
      if (err.response?.status === 429) toast(err.response?.data?.detail || 'Quota reached.', { description: 'Upgrade your plan to continue.', duration: 6000, action: { label: 'View Plans', onClick: () => window.location.href = '/dashboard/billing?tab=plans' } });
      else toast('Something went wrong.', { description: 'Please try again in a moment.', duration: 5000 });
    }
  };

  const handleExportCSV = async () => {
    try {
      const res = await api.post('/api/leads/export', { leads: [currentLead] }, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url;
      link.download = `${currentLead.company_name.replace(/[^a-zA-Z0-9]/g, '_')}.csv`;
      document.body.appendChild(link); link.click(); link.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Lead exported to CSV!', { duration: 4000 });
    } catch { toast('Export failed.', { description: 'Please try again or use a different format.', duration: 5000 }); }
  };

  const handleExportPdf = async () => {
    // Free and Starter plans: show upgrade banner instead
    if (plan === 'free' || plan === 'starter') {
      toast('PDF Reports require Growth plan or higher', {
        description: 'Get detailed lead reports with scores, pitch angles, and market intelligence.',
        duration: 8000,
        action: { label: 'View Plans', onClick: () => window.location.href = '/dashboard/billing?tab=plans' },
      });
      return;
    }
    setExportingPdf(true);
    try {
      const res = await api.get(`/api/leads/lead-report/${encodeURIComponent(currentLead.company_name)}`, {
        responseType: 'blob',
      });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      const link = document.createElement('a');
      link.href = url;
      link.download = `${currentLead.company_name.replace(/[^a-zA-Z0-9]/g, '_')}_iLeadX_Report.pdf`;
      document.body.appendChild(link); link.click(); link.remove();
      window.URL.revokeObjectURL(url);
      toast.success('PDF report downloaded!');
    } catch (err) {
      if (err.response?.status === 403) {
        toast('PDF reports require a paid plan', { description: 'Upgrade to download detailed lead reports.', duration: 6000 });
      } else {
        toast.error('Failed to generate PDF report.');
      }
    } finally { setExportingPdf(false); }
  };

  const handleExportGSheet = async () => {
    setExportingSheets(true);
    try {
      const res = await api.post('/api/leads/export-gsheet', {
        leads: [currentLead],
        sheet_name: `iLeadX - ${currentLead.company_name}`
      });
      if (res.data.success) {
        toast.success('Lead exported to Google Sheets!', {
          action: { label: 'Open Sheet', onClick: () => window.open(res.data.sheet_url, '_blank') },
          duration: Infinity,
          dismissible: true,
        });
      } else {
        toast.error(res.data.error || 'Export failed', { duration: 6000 });
      }
    } catch (err) {
      if (err.response?.status === 401) {
        toast('Google Sheets not connected.', {
          description: 'Connect it in Integrations to enable exports.',
          duration: 8000,
          action: { label: 'Go to Integrations', onClick: () => navigate('/dashboard/integrations') },
        });
      } else {
        toast.error('Export failed.', { duration: 5000 });
      }
    }
    finally { setExportingSheets(false); }
  };

  const doBoth = async () => {
    if (processingRef.current) return;
    processingRef.current = true;
    setReProcessing(true);
    // Persist to sessionStorage so state survives refresh
    try { sessionStorage.setItem('ileadx_processing_' + currentLead.company_name, JSON.stringify({ company: currentLead.company_name, started: Date.now() })); } catch {}
    try {
      const res = await api.post('/api/leads/regenerate-score', { company_name: currentLead.company_name, search_query: currentLead.search_query, service_context: currentLead.service_context || 'B2B software', enrich_only: false, score_also: true, rescrape: true });
      if (res.data?.status === 'already_processing') {
        processingRef.current = false;
        setReProcessing(false);
        try { sessionStorage.removeItem('ileadx_processing_' + currentLead.company_name); } catch {}
        toast('Already processing this lead.', { description: 'A job is already running. Please wait for it to complete.', duration: 5000 });
        return;
      }
      toast('Job queued — processing on workers');
    } catch (err) {
      processingRef.current = false;
      setReProcessing(false);
      if (err.response?.status === 429) toast(err.response?.data?.detail || 'Quota reached.', { description: 'Upgrade your plan to continue.', duration: 6000, action: { label: 'View Plans', onClick: () => window.location.href = '/dashboard/billing?tab=plans' } });
      else toast('Something went wrong.', { description: 'Please try again in a moment.', duration: 5000 });
    }
  };

  const handleReassign = async () => {
    if (!newOwnerId || !currentLead?.company_name) return;
    setReassigning(true);
    try {
      await api.patch('/api/leads/reassign', {
        company_name: currentLead.company_name,
        new_sdr_owner_id: parseInt(newOwnerId, 10),
      });
      toast.success('Lead reassigned');
      setNewOwnerId('');
      fetchLeadData(currentLead.company_name);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Reassign failed');
    } finally {
      setReassigning(false);
    }
  };

  // ─── RENDER ───
  return (
    <div style={{ maxWidth: 960, margin: '0 auto', padding: "0 4px" }}>

      {/* Back button */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
        <button onClick={onBack} style={{
          background: 'none', border: 'none', color: 'var(--text-secondary)',
          cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 6,
          fontSize: 'var(--text-sm)', padding: '8px 0',
        }}
          onMouseEnter={e => e.currentTarget.style.color = 'var(--text-primary)'}
          onMouseLeave={e => e.currentTarget.style.color = 'var(--text-secondary)'}>
          <ArrowLeft size={16} /> Back to Leads
        </button>
        <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
          <button onClick={handleExportCSV} title="Export to CSV" style={{
            display: 'inline-flex', alignItems: 'center', gap: 4,
            padding: '4px 8px', borderRadius: 'var(--radius-md)',
            border: '1px solid var(--success)', background: 'transparent',
            color: 'var(--success)', cursor: 'pointer',
            fontSize: 10, fontWeight: 500,
          }}>
            <Download size={12} /> CSV
          </button>
          <button onClick={handleExportGSheet} disabled={exportingSheets} title="Export to Google Sheets" style={{
            display: 'inline-flex', alignItems: 'center', gap: 4,
            padding: '4px 8px', borderRadius: 'var(--radius-md)',
            border: '1px solid #34a853', background: 'transparent',
            color: '#34a853', cursor: exportingSheets ? 'wait' : 'pointer',
            fontSize: 10, fontWeight: 500,
            opacity: exportingSheets ? 0.7 : 1,
          }}>
            {exportingSheets ? <LoadingDots /> : (
              <img src="https://upload.wikimedia.org/wikipedia/commons/3/30/Google_Sheets_logo_%282014-2020%29.svg" alt="" style={{ width: 14, height: 14 }} />
            )}
            {exportingSheets ? '' : 'Sheets'}
          </button>
          <button onClick={handleExportPdf} disabled={exportingPdf} title={(plan === 'free' || plan === 'starter') ? 'Upgrade to Growth for PDF reports' : 'Download PDF Report'} style={{
            display: 'inline-flex', alignItems: 'center', gap: 4,
            padding: '4px 8px', borderRadius: 'var(--radius-md)',
            border: '1px solid var(--border-primary)',
            background: 'transparent',
            color: 'var(--text-secondary)',
            cursor: exportingPdf ? 'wait' : 'pointer',
            fontSize: 10, fontWeight: 500,
            opacity: exportingPdf ? 0.7 : 1,
          }}>
            {(plan === 'free' || plan === 'starter') ? <><Lock size={12} /> PDF</> : exportingPdf ? <LoadingDots /> : <><FileText size={12} /> PDF</>}
          </button>

        </div>
      </div>

      <style>{`
        @media (max-width: 640px) {
          .hero-responsive {
            grid-template-columns: 1fr 1fr !important;
            gap: 12px !important;
            padding: 16px !important;
          }
          .hero-responsive > *:first-child { grid-row: 1; }
          .hero-responsive > *:last-child { grid-row: 1; justify-self: end; }
          .hero-responsive > *:nth-child(2) { grid-column: 1 / -1; grid-row: 2; }
        }
      `}</style>
      {/* ─── HERO ─── */}
      <div className="hero-responsive" style={{
        display: 'grid', gridTemplateColumns: 'auto 1fr auto', gap: 24,
        alignItems: 'center', background: 'var(--bg-secondary)',
        border: '1px solid var(--border-primary)',
        borderRadius: 'var(--radius-xl)', padding: '24px 28px',
        boxShadow: 'var(--shadow-md)',
        marginBottom: 'var(--space-2xl)',
      }}>

        {/* Avatar */}
        <div style={{
          width: 80, height: 80, borderRadius: 'var(--radius-xl)',
          background: currentLead.logo_url ? 'var(--bg-tertiary)' : 'var(--accent-gradient)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          color: currentLead.logo_url ? 'var(--text-primary)' : 'white',
          fontSize: 'var(--text-2xl)', fontWeight: 700, flexShrink: 0,
          boxShadow: 'var(--shadow-glow)',
        }}>
          {currentLead.logo_url ? (
            <img src={currentLead.logo_url} alt="" onError={(e) => { e.target.style.display = 'none'; e.target.parentElement.innerHTML = initials; }}
              style={{ width: '100%', height: '100%', borderRadius: 'var(--radius-xl)', objectFit: 'contain', padding: 8, background: 'var(--bg-tertiary)' }} />
          ) : initials}
        </div>

        {/* Center: Name & Meta */}
        <div>
          <h1 style={{ fontSize: 'var(--text-xl)', fontWeight: 700, color: 'var(--text-primary)', margin: 0, letterSpacing: '-0.3px' }}>
            {currentLead.company_name}
          </h1>
          <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', marginTop: 4 }}>
            {currentLead.industry && (
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, color: 'var(--text-secondary)', fontSize: 'var(--text-sm)' }}>
                <Briefcase size={14} /> {currentLead.industry}
              </span>
            )}
            {currentLead.google_rating && (
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, color: 'var(--text-secondary)', fontSize: 'var(--text-sm)' }}>
                <Star size={14} fill="#f59e0b" color="#f59e0b" /> {currentLead.google_rating} ({currentLead.google_review_count || 0})
              </span>
            )}
            {currentLead.scraped_at && (
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, color: 'var(--text-tertiary)', fontSize: 'var(--text-xs)' }}>
                <Clock size={14} /> {(currentLead.scraped_at && !currentLead.scraped_at.endsWith('Z') && !currentLead.scraped_at.includes('+')) ? new Date(currentLead.scraped_at + 'Z').toLocaleString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit' }) : new Date(currentLead.scraped_at).toLocaleString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit' })}
              </span>
            )}
          </div>
          {currentLead.service_context && (
            <div style={{ marginTop: 6 }}>
              <span style={{
                display: 'inline-flex', alignItems: 'center', gap: 6,
                padding: '2px 12px', borderRadius: 'var(--radius-full)',
                background: 'var(--bg-tertiary)', border: '1px solid var(--border-primary)',
                color: 'var(--text-secondary)', fontSize: 'var(--text-xs)', fontWeight: 500,
              }}>
                <Target size={12} style={{ color: 'var(--accent-primary)' }} />
                Scored for: <strong style={{ color: 'var(--text-primary)', fontWeight: 600 }}>{currentLead.service_context}</strong>
              </span>
            </div>
          )}
        </div>

        {/* Score Ring */}
        <div style={{ textAlign: 'center', flexShrink: 0 }}>
            <div style={{ position: 'relative', width: 80, height: 80 }}>
              <svg width="80" height="80" viewBox="0 0 80 80">
                <circle cx="40" cy="40" r="32" fill="none" stroke="var(--border-primary)" strokeWidth="6" />
                <circle cx="40" cy="40" r="32" fill="none" stroke={scoreColor} strokeWidth="6"
                  strokeDasharray="201"
                  strokeDashoffset={201 - (score / 100) * 201}
                  strokeLinecap="round"
                  style={{ transition: 'stroke-dashoffset 0.6s ease' }}
                />
              </svg>
              <div style={{ position: 'absolute', inset: 0, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
                <span style={{ fontSize: 22, fontWeight: 800, color: scoreColor, lineHeight: 1 }}>{score || '—'}</span>
                <span style={{ fontSize: 8, fontWeight: 500, color: scoreColor, textTransform: 'uppercase', letterSpacing: '0.05em' }}>Score</span>
              </div>
            </div>
          {urgency && (
            <div style={{
              display: 'inline-flex', alignItems: 'center', gap: 4,
              padding: '2px 10px', borderRadius: 20,
              background: urgencyBg, border: `1px solid ${urgencyColor}`,
              marginTop: 6,
            }}>
              <span style={{ width: 6, height: 6, borderRadius: '50%', background: urgencyColor, flexShrink: 0 }}></span>
              <span style={{ fontSize: 10, fontWeight: 700, color: urgencyColor }}>{urgencyLabel}</span>
            </div>
          )}
          <p style={{ color: scoreColor, fontSize: 'var(--text-xs)', fontWeight: 500, margin: '4px 0 0' }}>
            {scoreLabel}
          </p>
        </div>
      </div>

      {/* ─── LEAD OWNERSHIP & REASSIGN (Enterprise) ─── */}
      {user?.organization_id && ['owner','admin'].includes(user?.team_role) && teamMembers.length > 0 && (
        <div style={{
          background: 'var(--bg-secondary)', border: '1px solid var(--border-primary)',
          borderRadius: 'var(--radius-xl)', padding: '14px 20px',
          boxShadow: 'var(--shadow-sm)', marginBottom: 'var(--space-xl)',
          display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap',
        }}>
          <span style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-secondary)' }}>Lead Owner:</span>
          <select
            value={newOwnerId || currentLead.sdr_owner_id || ''}
            onChange={(e) => setNewOwnerId(e.target.value)}
            style={{
              padding: '8px 12px', borderRadius: 8,
              border: '1px solid var(--border-primary)',
              background: 'var(--input-bg)',
              color: 'var(--text-primary)',
              fontSize: 12, outline: 'none',
              cursor: 'pointer', fontFamily: 'inherit',
              flex: 1, minWidth: 180,
            }}
          >
            <option value="">Select SDR</option>
            {teamMembers.map(m => (
              <option key={m.id} value={m.id}>{m.full_name || m.email} ({m.team_role})</option>
            ))}
          </select>
          <button
            onClick={handleReassign}
            disabled={!newOwnerId || reassigning}
            style={{
              padding: '8px 14px', borderRadius: 8,
              border: 'none', background: 'var(--accent-gradient)',
              color: 'white', cursor: reassigning ? 'not-allowed' : 'pointer',
              fontSize: 12, fontWeight: 600,
              opacity: reassigning ? 0.6 : 1,
            }}
          >
            {reassigning ? 'Reassigning...' : 'Reassign Lead'}
          </button>
        </div>
      )}

      {/* ─── STATUS HISTORY ─── */}
      {statusHistory.length > 0 && (
        <div style={{
          background: 'var(--bg-secondary)', border: '1px solid var(--border-primary)',
          borderRadius: 'var(--radius-xl)', padding: '20px 24px',
          boxShadow: 'var(--shadow-sm)', marginBottom: 'var(--space-xl)',
        }}>
          <h3 style={{ color: 'var(--text-primary)', fontSize: 'var(--text-base)', fontWeight: 700, margin: '0 0 16px', display: 'flex', alignItems: 'center', gap: 8 }}>
            <Activity size={18} color="var(--accent-primary)" />
            Status History
          </h3>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            {statusHistory.map((h, i) => (
              <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 12px', background: 'var(--bg-tertiary)', borderRadius: 10, border: '1px solid var(--border-primary)' }}>
                <span style={{ width: 8, height: 8, borderRadius: '50%', background: 'var(--accent-primary)', flexShrink: 0 }} />
                <span style={{ fontSize: 13, color: 'var(--text-secondary)', flex: 1 }}>
                  <strong style={{ color: 'var(--text-primary)' }}>{h.old_status}</strong> → <strong style={{ color: 'var(--accent-primary)' }}>{h.new_status}</strong> by {h.full_name || h.email || 'Unknown'}
                </span>
                <span style={{ fontSize: 10, color: 'var(--text-tertiary)' }}>{h.created_at ? new Date(h.created_at + 'Z').toLocaleString() : ''}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* ─── LEAD SUMMARY ─── */}
      {currentLead.narrative && (
        <div style={{
          background: 'var(--bg-secondary)', border: '1px solid var(--border-primary)',
          borderRadius: 'var(--radius-xl)', padding: '20px 24px',
          boxShadow: 'var(--shadow-md)', marginBottom: 'var(--space-xl)',
          borderLeft: '4px solid var(--accent-amber)',
        }}>
          <h3 style={{ color: 'var(--accent-amber)', fontSize: 'var(--text-sm)', fontWeight: 700, margin: '0 0 8px', display: 'flex', alignItems: 'center', gap: 8, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
            <Sparkles size={16} /> Lead Summary
          </h3>
          <p style={{ color: 'var(--text-primary)', fontSize: 'var(--text-sm)', margin: 0, lineHeight: 1.7 }}>
            {currentLead.narrative}
          </p>
        </div>
      )}

      {/* ─── V4 SCORE BREAKDOWN ─── */}
      {hasV4Data && (
      <div style={{
        background: 'var(--bg-secondary)', border: '1px solid var(--border-primary)',
        borderRadius: 'var(--radius-xl)', padding: '20px 24px',
        boxShadow: 'var(--shadow-sm)', marginBottom: 'var(--space-xl)',
      }}>
        <h3 style={{ color: 'var(--text-primary)', fontSize: 'var(--text-base)', fontWeight: 700, margin: '0 0 16px', display: 'flex', alignItems: 'center', gap: 8 }}>
          <Activity size={18} color="var(--accent-primary)" />
          Score Breakdown
        </h3>
        
        {/* Business Quality + Service Fit bars */}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 20 }}>
          <div style={{ textAlign: 'center' }}>
            <div style={{ fontSize: 10, color: 'var(--text-tertiary)', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 6 }}>
              <Building size={12} style={{ marginRight: 4 }} /> Business Quality
            </div>
            <div style={{ position: 'relative', height: 8, background: 'var(--border-primary)', borderRadius: 4, overflow: 'hidden', marginBottom: 4 }}>
              <div style={{ height: '100%', width: `${businessQuality}%`, background: 'linear-gradient(90deg, var(--info), var(--accent-primary))', borderRadius: 4, transition: 'width 1s ease' }} />
            </div>
            <span style={{ fontSize: 14, fontWeight: 700, color: 'var(--text-primary)' }}>{businessQuality}</span>
          </div>
          <div style={{ textAlign: 'center' }}>
            <div style={{ fontSize: 10, color: 'var(--text-tertiary)', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 6 }}>
              <Target size={12} style={{ marginRight: 4 }} /> Service Fit
            </div>
            <div style={{ position: 'relative', height: 8, background: 'var(--border-primary)', borderRadius: 4, overflow: 'hidden', marginBottom: 4 }}>
              <div style={{ height: '100%', width: `${serviceFit}%`, background: serviceFit >= 70 ? 'linear-gradient(90deg, var(--success), #10b981)' : serviceFit >= 40 ? 'linear-gradient(90deg, var(--warning), #f59e0b)' : 'linear-gradient(90deg, var(--error), #ef4444)', borderRadius: 4, transition: 'width 1s ease' }} />
            </div>
            <span style={{ fontSize: 14, fontWeight: 700, color: 'var(--text-primary)' }}>{serviceFit}</span>
          </div>
        </div>

        {/* Raised by / Lowered by / To improve - Growth+ only */}
        {(plan === 'growth' || plan === 'pro' || plan === 'enterprise') && scoreBreakdown.raised_by && scoreBreakdown.raised_by.length > 0 && (
          <div style={{ marginBottom: 12 }}>
            <div style={{ fontSize: 10, color: 'var(--success)', fontWeight: 500, textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 6, display: 'flex', alignItems: 'center', gap: 4 }}>
              <ThumbsUp size={12} /> Raised By
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
              {scoreBreakdown.raised_by.map((item, i) => (
                <div key={i} style={{ fontSize: 12, color: 'var(--text-secondary)', paddingLeft: 16, position: 'relative' }}>
                  <span style={{ position: 'absolute', left: 0, color: 'var(--success)', fontSize: 10 }}>+</span>
                  {item}
                </div>
              ))}
            </div>
          </div>
        )}
        
        {(plan === 'growth' || plan === 'pro' || plan === 'enterprise') && scoreBreakdown.lowered_by && scoreBreakdown.lowered_by.length > 0 && (
          <div style={{ marginBottom: 12 }}>
            <div style={{ fontSize: 10, color: 'var(--error)', fontWeight: 500, textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 6, display: 'flex', alignItems: 'center', gap: 4 }}>
              <ThumbsDown size={12} /> Lowered By
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
              {scoreBreakdown.lowered_by.map((item, i) => (
                <div key={i} style={{ fontSize: 12, color: 'var(--text-secondary)', paddingLeft: 16, position: 'relative' }}>
                  <span style={{ position: 'absolute', left: 0, color: 'var(--error)', fontSize: 10 }}>−</span>
                  {item}
                </div>
              ))}
            </div>
          </div>
        )}
        
        {(plan === 'growth' || plan === 'pro' || plan === 'enterprise') && scoreBreakdown.to_improve && (
          <div style={{ padding: '10px 14px', borderRadius: 'var(--radius-md)', background: 'var(--info-bg)', border: '1px solid rgba(59,130,246,0.2)' }}>
            <div style={{ fontSize: 10, color: 'var(--info)', fontWeight: 500, textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 4, display: 'flex', alignItems: 'center', gap: 4 }}>
              <Info size={12} /> To Improve
            </div>
            <p style={{ fontSize: 12, color: 'var(--text-primary)', margin: 0, lineHeight: 1.6 }}>{scoreBreakdown.to_improve}</p>
          </div>
        )}
      </div>
      )}

      {/* ─── SERVICE-COMPLAINT MAPPING ─── */}
      <div style={{
        background: 'var(--bg-secondary)', border: '1px solid var(--border-primary)',
        borderRadius: 'var(--radius-xl)', padding: '20px 24px',
        boxShadow: 'var(--shadow-sm)', marginBottom: 'var(--space-xl)',
      }}>
        <h3 style={{ color: 'var(--text-primary)', fontSize: 'var(--text-base)', fontWeight: 700, margin: '0 0 16px', display: 'flex', alignItems: 'center', gap: 8 }}>
          <ShieldCheck size={18} color="var(--accent-secondary)" />
          Service Fit Analysis
        </h3>
        {plan === 'free' || plan === 'starter' ? (
          <div style={{ textAlign: 'center', padding: '24px 16px' }}>
            <div style={{ width: 48, height: 48, borderRadius: '50%', background: 'var(--warning-bg)', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 12px' }}>
              <Lock size={22} color="var(--warning)" />
            </div>
            <p style={{ color: 'var(--text-secondary)', fontSize: 13, margin: '0 0 4px', lineHeight: 1.6 }}>
              Know exactly how your business fits into this lead's situation.
            </p>
            <p style={{ color: 'var(--text-tertiary)', fontSize: 12, margin: '0 0 16px' }}>
              Get complaint-to-service mapping, severity analysis, liability relevance scoring, and evidence chain — all tailored to your service context.
            </p>
            <a href="/dashboard/billing?tab=plans" style={{
              display: 'inline-flex', alignItems: 'center', gap: 6,
              padding: '8px 20px', borderRadius: 8,
              background: 'transparent', border: '1px solid var(--border-primary)', color: 'var(--text-secondary)',
              textDecoration: 'none', fontSize: 13, fontWeight: 500,
            }}>
              Upgrade to Growth <ArrowRight size={14} />
            </a>
          </div>
        ) : Object.keys(serviceComplaintMapping).length > 0 ? (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {/* Individual complaint themes */}
          {serviceComplaintMapping.top_complaints && serviceComplaintMapping.top_complaints.length > 0 && (
            <div style={{ marginBottom: 12 }}>
              <div style={{ fontSize: 10, color: 'var(--text-tertiary)', fontWeight: 500, textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 8 }}>Top Complaints</div>
              {serviceComplaintMapping.top_complaints.map((c, i) => (
                <div key={i} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px 0', borderBottom: i < serviceComplaintMapping.top_complaints.length - 1 ? '1px solid var(--border-primary)' : 'none' }}>
                  <div style={{ flex: 1 }}>
                    <span style={{ color: 'var(--text-primary)', fontSize: 12, fontWeight: 500 }}>{c.theme}</span>
                    <span style={{ color: 'var(--text-tertiary)', fontSize: 10, marginLeft: 8 }}>{c.pct}% ({c.count}/{c.total})</span>
                  </div>
                  <span style={{
                    padding: '2px 8px', borderRadius: 'var(--radius-full)', fontSize: 10, fontWeight: 500,
                    background: c.liability_relevance?.includes('High') ? 'var(--error-bg)' : c.liability_relevance?.includes('Moderate') ? 'var(--warning-bg)' : 'var(--bg-tertiary)',
                    color: c.liability_relevance?.includes('High') ? 'var(--error)' : c.liability_relevance?.includes('Moderate') ? 'var(--warning)' : 'var(--text-secondary)',
                  }}>{c.liability_relevance || 'N/A'}</span>
                </div>
              ))}
            </div>
          )}
          {serviceComplaintMapping.highest_liability_risk && (
            <div style={{ padding: '8px 12px', borderRadius: 'var(--radius-md)', background: 'var(--warning-bg)', border: '1px solid rgba(245,158,11,0.2)', marginBottom: 10 }}>
              <span style={{ fontSize: 10, color: 'var(--warning)', fontWeight: 600 }}>Highest Risk: </span>
              <span style={{ fontSize: 11, color: 'var(--text-primary)' }}>{serviceComplaintMapping.highest_liability_risk}</span>
            </div>
          )}
          <div style={{ display: 'flex', justifyContent: 'space-between', padding: '6px 0', borderBottom: '1px solid var(--border-primary)' }}>
            <span style={{ color: 'var(--text-tertiary)', fontSize: 11 }}>Overall Severity</span>
            <span style={{
              padding: '2px 10px', borderRadius: 'var(--radius-full)', fontSize: 11, fontWeight: 500,
              background: serviceComplaintMapping.overall_severity === 'Critical' ? 'var(--error-bg)' : serviceComplaintMapping.overall_severity === 'High' ? 'var(--warning-bg)' : 'var(--bg-tertiary)',
              color: serviceComplaintMapping.overall_severity === 'Critical' ? 'var(--error)' : serviceComplaintMapping.overall_severity === 'High' ? 'var(--warning)' : 'var(--text-secondary)',
            }}>{serviceComplaintMapping.overall_severity || serviceComplaintMapping.severity || 'N/A'}</span>
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', padding: '6px 0', borderBottom: '1px solid var(--border-primary)' }}>
            <span style={{ color: 'var(--text-tertiary)', fontSize: 11 }}>Service Relevance</span>
            <span style={{
              padding: '2px 10px', borderRadius: 'var(--radius-full)', fontSize: 11, fontWeight: 500,
              background: serviceComplaintMapping.overall_service_relevance?.includes('Direct') ? 'var(--success-bg)' : serviceComplaintMapping.overall_service_relevance?.includes('Indirect') ? 'var(--warning-bg)' : 'var(--bg-tertiary)',
              color: serviceComplaintMapping.overall_service_relevance?.includes('Direct') ? 'var(--success)' : serviceComplaintMapping.overall_service_relevance?.includes('Indirect') ? 'var(--warning)' : 'var(--text-secondary)',
            }}>{serviceComplaintMapping.overall_service_relevance || serviceComplaintMapping.service_relevance || 'N/A'}</span>
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', padding: '6px 0', borderBottom: '1px solid var(--border-primary)' }}>
            <span style={{ color: 'var(--text-tertiary)', fontSize: 11 }}>Mapping Strength</span>
            <span style={{
              padding: '2px 10px', borderRadius: 'var(--radius-full)', fontSize: 11, fontWeight: 500,
              background: serviceComplaintMapping.mapping_strength === 'Strong' ? 'var(--success-bg)' : serviceComplaintMapping.mapping_strength === 'Weak' ? 'var(--error-bg)' : 'var(--warning-bg)',
              color: serviceComplaintMapping.mapping_strength === 'Strong' ? 'var(--success)' : serviceComplaintMapping.mapping_strength === 'Weak' ? 'var(--error)' : 'var(--warning)',
            }}>{serviceComplaintMapping.mapping_strength || 'N/A'}</span>
          </div>
          {serviceComplaintMapping.evidence_chain && (
            <div style={{ padding: '10px 14px', borderRadius: 'var(--radius-md)', background: 'var(--bg-tertiary)', border: '1px solid var(--border-primary)', marginTop: 4 }}>
              <p style={{ fontSize: 12, color: 'var(--text-secondary)', margin: 0, lineHeight: 1.6, fontStyle: 'italic' }}>
                {serviceComplaintMapping.evidence_chain}
              </p>
            </div>
          )}
        </div>
        ) : (
          <div style={{ textAlign: 'center', padding: '24px 16px' }}>
            <p style={{ color: 'var(--text-tertiary)', fontSize: 13 }}>No complaint data available for this lead yet. Run Enrich & Score to populate.</p>
          </div>
        )}
      </div>

      {/* ─── HONEST ASSESSMENT (when Service Fit is weak) ─── */}
      {honestAssessment && (
      <div style={{
        background: 'var(--bg-secondary)', border: '1px solid var(--warning)',
        borderRadius: 'var(--radius-xl)', padding: '20px 24px',
        boxShadow: 'var(--shadow-sm)', marginBottom: 'var(--space-xl)',
        borderLeft: '4px solid var(--warning)',
      }}>
        <h3 style={{ color: 'var(--warning)', fontSize: 'var(--text-base)', fontWeight: 700, margin: '0 0 12px', display: 'flex', alignItems: 'center', gap: 8 }}>
          <AlertTriangle size={18} />
          Honest Assessment
        </h3>
        <p style={{ color: 'var(--text-primary)', fontSize: 'var(--text-sm)', margin: '0 0 12px', lineHeight: 1.7 }}>
          {honestAssessment}
        </p>
        {triggerEvents.length > 0 && (
          <div>
            <div style={{ fontSize: 10, color: 'var(--text-tertiary)', fontWeight: 500, textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 6 }}>
              Trigger Events to Watch
            </div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
              {triggerEvents.map((event, i) => (
                <span key={i} style={{
                  padding: '4px 12px', borderRadius: 'var(--radius-full)',
                  background: 'var(--warning-bg)', color: 'var(--warning)',
                  fontSize: 11, fontWeight: 500, border: '1px solid rgba(245,158,11,0.2)',
                }}>
                  {event}
                </span>
              ))}
            </div>
          </div>
        )}
      </div>
      )}

      {/* ─── PITCH ANGLE ─── */}
      <div style={{
        background: 'var(--bg-secondary)', border: '1px solid var(--border-primary)',
        borderRadius: 'var(--radius-xl)', padding: '20px 24px',
        boxShadow: 'var(--shadow-sm)', marginBottom: 'var(--space-2xl)',
      }}>
        <h3 style={{ color: 'var(--text-primary)', fontSize: 'var(--text-base)', fontWeight: 700, margin: '0 0 16px', display: 'flex', alignItems: 'center', gap: 8 }}>
          <Sparkles size={18} color="var(--accent-amber)" />
          Pitch Angle
        </h3>
        {pitchAngles.length > 0 ? (
          <div>
            {pitchAngles.length > 1 && (
              <div style={{ display: "flex", gap: 4, flexWrap: "wrap", marginBottom: 12 }}>
                {pitchAngles.map((angle, i) => (
                  <button key={i} onClick={() => setActivePitchTab(i)} style={{
                    padding: "4px 12px", borderRadius: 'var(--radius-full)', border: 'none',
                    background: activePitchTab === i ? 'var(--accent-amber)' : 'var(--bg-tertiary)',
                    color: activePitchTab === i ? '#0F172A' : 'var(--text-secondary)',
                    cursor: 'pointer', fontSize: "var(--text-xs)", fontWeight: 500, fontFamily: 'inherit',
                  }}>
                    {angle.label}
                  </button>
                ))}
              </div>
            )}
            <div style={{
              background: 'var(--bg-primary)', borderRadius: 'var(--radius-md)',
              padding: '16px 20px', border: '1px solid var(--border-primary)',
              fontSize: 'var(--text-sm)', color: 'var(--text-secondary)',
              lineHeight: 1.7, fontStyle: 'italic', marginBottom: 12,
            }}>
              "{pitchAngles[activePitchTab]?.text || pitchAngles[0]?.text}"
            </div>
            <button onClick={() => {
              navigator.clipboard.writeText(pitchAngles[activePitchTab]?.text || '');
              setCopied(true);
              toast.success('Pitch copied!');
              setTimeout(() => setCopied(false), 2000);
            }} style={{
              padding: '8px 16px', borderRadius: 8, border: copied ? '1px solid var(--success)' : '1px solid var(--accent-amber)',
              background: copied ? 'var(--success-bg)' : 'transparent', 
              color: copied ? 'var(--success)' : 'var(--accent-amber)',
              cursor: 'pointer', fontSize: 12, fontWeight: 500, fontFamily: 'inherit',
              display: 'inline-flex', alignItems: 'center', gap: 6, whiteSpace: 'nowrap',
              transition: 'all 0.2s ease',
            }}>
              {copied ? <><Check size={14} /> Pitch Copied!</> : <><Copy size={14} /> Copy Pitch</>}
            </button>
          </div>
        ) : (
          <p style={{ color: 'var(--text-tertiary)', fontSize: 'var(--text-sm)', textAlign: 'center', padding: '24px 0' }}>
            Your service doesn't fit this business at this time. Try rescore after 2 weeks.
          </p>
        )}
      </div>

      {/* ─── TWO-COLUMN LAYOUT: Contact + AI Insights ─── */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 360px), 1fr))',
        gap: 'var(--space-xl)',
        marginBottom: 'var(--space-2xl)'
      }}>

        {/* ─── LEFT: Contact ─── */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-xl)' }}>

          <div style={{
            background: 'var(--bg-secondary)', border: '1px solid var(--border-primary)',
            borderRadius: 'var(--radius-xl)', padding: '20px 24px',
            boxShadow: 'var(--shadow-sm)',
          }}>
            <h4 style={{
              display: 'flex', alignItems: 'center', gap: 8,
              color: 'var(--text-secondary)', fontSize: 'var(--text-xs)', fontWeight: 500,
              textTransform: 'uppercase', letterSpacing: '0.05em',
              margin: '0 0 16px', paddingBottom: 12, borderBottom: '1px solid var(--border-primary)',
            }}>
              <Phone size={16} /> Contact Information
            </h4>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {(currentLead.phones || currentLead.phone) && (() => {
                const raw = (currentLead.phones ? currentLead.phones.split(/,\s*/) : [currentLead.phone]).filter(Boolean).map(p => p.trim());
                const all = [...new Set(raw)]; const total = all.length;
                let show, hidden;
                if (plan === 'free' || plan === 'starter') { show = all.slice(0, 1); hidden = total - 1; }
                else if (plan === 'growth') { show = all.slice(0, 3); hidden = total - 3; }
                else { show = all; hidden = 0; }
                return (
                <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '8px 12px', borderRadius: 'var(--radius-md)', background: 'var(--bg-tertiary)' }}>
                  <span style={{ width: 36, height: 36, borderRadius: 'var(--radius-md)', background: 'var(--success-bg)', color: 'var(--success)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}><Phone size={16} /></span>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                    {show.map((p, i) => (<a key={i} href={`tel:${p}`} style={{ color: 'var(--accent-link)', fontSize: 'var(--text-sm)', fontWeight: 500, textDecoration: 'none', wordBreak: 'break-all' }}>{p}</a>))}
                    {hidden > 0 && (plan === 'free' || plan === 'starter') && (<span style={{ color: 'var(--text-tertiary)', fontSize: 11, fontStyle: 'italic' }}>+{hidden} more on higher plans</span>)}
                    {hidden > 0 && plan === 'growth' && (<a href="/dashboard/billing?tab=plans" style={{ color: 'var(--accent-primary)', fontSize: 11, fontWeight: 500, textDecoration: 'none' }}>+{hidden} more →</a>)}
                  </div>
                </div>);
              })()}
              {(currentLead.emails || currentLead.email) && (() => {
                const raw = (currentLead.emails ? currentLead.emails.split(/,\s*/) : [currentLead.email]).filter(Boolean).map(e => e.trim());
                const all = [...new Set(raw)]; const total = all.length;
                let show, hidden;
                if (plan === 'free' || plan === 'starter') { show = all.slice(0, 1); hidden = total - 1; }
                else if (plan === 'growth') { show = all.slice(0, 2); hidden = total - 2; }
                else { show = all; hidden = 0; }
                return (
                <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '8px 12px', borderRadius: 'var(--radius-md)', background: 'var(--bg-tertiary)' }}>
                  <span style={{ width: 36, height: 36, borderRadius: 'var(--radius-md)', background: 'var(--info-bg)', color: 'var(--info)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}><Mail size={16} /></span>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                    {show.map((e, i) => (<a key={i} href={`mailto:${e}`} style={{ color: 'var(--accent-link)', fontSize: 'var(--text-sm)', fontWeight: 500, textDecoration: 'none', wordBreak: 'break-all' }}>{e}</a>))}
                    {hidden > 0 && (plan === 'free' || plan === 'starter') && (<span style={{ color: 'var(--text-tertiary)', fontSize: 11, fontStyle: 'italic' }}>+{hidden} more on higher plans</span>)}
                    {hidden > 0 && plan === 'growth' && (<a href="/dashboard/billing?tab=plans" style={{ color: 'var(--accent-primary)', fontSize: 11, fontWeight: 500, textDecoration: 'none' }}>+{hidden} more →</a>)}
                  </div>
                </div>);
              })()}
              {currentLead.website && (
                <a href={currentLead.website} target="_blank" rel="noopener noreferrer" style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '8px 12px', borderRadius: 'var(--radius-md)', background: 'var(--bg-tertiary)', textDecoration: 'none' }}>
                  <span style={{ width: 36, height: 36, borderRadius: 'var(--radius-md)', background: 'var(--accent-glow)', color: 'var(--accent-primary)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                    <Globe size={16} />
                  </span>
                  <span style={{ color: 'var(--accent-link)', fontSize: 'var(--text-sm)', fontWeight: 500, wordBreak: 'break-all' }}>
                    {currentLead.website.replace(/^https?:\/\//, '')} <ExternalLink size={11} style={{ opacity: 0.5 }} />
                  </span>
                </a>
              )}
              {currentLead.contact_form_url && (
                <a href={currentLead.contact_form_url} target="_blank" rel="noopener noreferrer" style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '8px 12px', borderRadius: 'var(--radius-md)', background: 'var(--bg-tertiary)', textDecoration: 'none' }}>
                  <span style={{ width: 36, height: 36, borderRadius: 'var(--radius-md)', background: 'var(--info-bg)', color: 'var(--info)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                    <MessageSquare size={16} />
                  </span>
                  <span style={{ color: 'var(--accent-link)', fontSize: 'var(--text-sm)', fontWeight: 500 }}>
                    Contact Form <ExternalLink size={11} style={{ opacity: 0.5 }} />
                  </span>
                </a>
              )}
              {currentLead.contact_page_url && !currentLead.contact_form_url && (
                <a href={currentLead.contact_page_url} target="_blank" rel="noopener noreferrer" style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '8px 12px', borderRadius: 'var(--radius-md)', background: 'var(--bg-tertiary)', textDecoration: 'none' }}>
                  <span style={{ width: 36, height: 36, borderRadius: 'var(--radius-md)', background: 'var(--info-bg)', color: 'var(--info)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                    <MessageSquare size={16} />
                  </span>
                  <span style={{ color: 'var(--accent-link)', fontSize: 'var(--text-sm)', fontWeight: 500 }}>
                    Contact Page <ExternalLink size={11} style={{ opacity: 0.5 }} />
                  </span>
                </a>
              )}
              {currentLead.address && (
                <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '8px 12px', borderRadius: 'var(--radius-md)', background: 'var(--bg-tertiary)' }}>
                  <span style={{ width: 36, height: 36, borderRadius: 'var(--radius-md)', background: 'var(--bg-secondary)', color: 'var(--text-secondary)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                    <MapPin size={16} />
                  </span>
                  <span style={{ color: 'var(--text-secondary)', fontSize: 'var(--text-sm)' }}>
                    {[currentLead.address, currentLead.city, currentLead.state, currentLead.country].filter(Boolean).join(', ')}
                  </span>
                </div>
              )}
            </div>
            {socials.length > 0 && (
              <div style={{ marginTop: 16, paddingTop: 16, borderTop: '1px solid var(--border-primary)', display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                {socials.map((s, i) => <SocialLink key={i} platform={s.platform} url={s.url} />)}
              </div>
            )}
          </div>

          {/* Business Details */}
          <div style={{
            background: 'var(--bg-secondary)', border: '1px solid var(--border-primary)',
            borderRadius: 'var(--radius-xl)', padding: '20px 24px',
            boxShadow: 'var(--shadow-sm)',
          }}>
            <h4 style={{
              display: 'flex', alignItems: 'center', gap: 8,
              color: 'var(--text-secondary)', fontSize: 'var(--text-xs)', fontWeight: 500,
              textTransform: 'uppercase', letterSpacing: '0.05em',
              margin: '0 0 16px', paddingBottom: 12, borderBottom: '1px solid var(--border-primary)',
            }}>
              <Building size={16} /> Business Details
            </h4>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              {currentLead.contact_name && (
                <div style={{ display: 'flex', justifyContent: 'space-between', padding: '6px 0' }}>
                  <span style={{ color: 'var(--text-tertiary)', fontSize: 'var(--text-xs)', fontWeight: 500 }}>Contact</span>
                  <span style={{ color: 'var(--text-primary)', fontSize: 'var(--text-sm)', fontWeight: 500 }}>{currentLead.contact_name}</span>
                </div>
              )}
              {currentLead.job_title && (
                <div style={{ display: 'flex', justifyContent: 'space-between', padding: '6px 0' }}>
                  <span style={{ color: 'var(--text-tertiary)', fontSize: 'var(--text-xs)', fontWeight: 500 }}>Title</span>
                  <span style={{ color: 'var(--text-primary)', fontSize: 'var(--text-sm)', fontWeight: 500 }}>{currentLead.job_title}</span>
                </div>
              )}
              {currentLead.employee_count && (
                <div style={{ display: 'flex', justifyContent: 'space-between', padding: '6px 0' }}>
                  <span style={{ color: 'var(--text-tertiary)', fontSize: 'var(--text-xs)', fontWeight: 500 }}>Employees</span>
                  <span style={{ color: 'var(--text-primary)', fontSize: 'var(--text-sm)', fontWeight: 500 }}>{currentLead.employee_count}</span>
                </div>
              )}
              {currentLead.annual_revenue && (
                <div style={{ display: 'flex', justifyContent: 'space-between', padding: '6px 0' }}>
                  <span style={{ color: 'var(--text-tertiary)', fontSize: 'var(--text-xs)', fontWeight: 500 }}>Revenue</span>
                  <span style={{ color: 'var(--text-primary)', fontSize: 'var(--text-sm)', fontWeight: 500 }}>{currentLead.annual_revenue}</span>
                </div>
              )}
              {currentLead.price && (
                <div style={{ display: 'flex', justifyContent: 'space-between', padding: '6px 0' }}>
                  <span style={{ color: 'var(--text-tertiary)', fontSize: 'var(--text-xs)', fontWeight: 500 }}>Price Level</span>
                  <span style={{ color: 'var(--text-primary)', fontSize: 'var(--text-sm)', fontWeight: 500 }}>{currentLead.price}</span>
                </div>
              )}
              {currentLead.linkedin_followers && (
                <div style={{ display: 'flex', justifyContent: 'space-between', padding: '6px 0' }}>
                  <span style={{ color: 'var(--text-tertiary)', fontSize: 'var(--text-xs)', fontWeight: 500 }}>LinkedIn</span>
                  <span style={{ color: 'var(--text-primary)', fontSize: 'var(--text-sm)', fontWeight: 500 }}>{parseInt(currentLead.linkedin_followers).toLocaleString()} followers</span>
                </div>
              )}
              {currentLead.linkedin_tagline && (
                <div style={{ display: 'flex', justifyContent: 'space-between', padding: '6px 0' }}>
                  <span style={{ color: 'var(--text-tertiary)', fontSize: 'var(--text-xs)', fontWeight: 500 }}>Tagline</span>
                  <span style={{ color: 'var(--text-primary)', fontSize: 'var(--text-sm)', fontWeight: 500 }}>{currentLead.linkedin_tagline}</span>
                </div>
              )}
              {currentLead.linkedin_founded_year && (
                <div style={{ display: 'flex', justifyContent: 'space-between', padding: '6px 0' }}>
                  <span style={{ color: 'var(--text-tertiary)', fontSize: 'var(--text-xs)', fontWeight: 500 }}>Founded</span>
                  <span style={{ color: 'var(--text-primary)', fontSize: 'var(--text-sm)', fontWeight: 500 }}>{currentLead.linkedin_founded_year}</span>
                </div>
              )}
              {currentLead.linkedin_company_type && (
                <div style={{ display: 'flex', justifyContent: 'space-between', padding: '6px 0' }}>
                  <span style={{ color: 'var(--text-tertiary)', fontSize: 'var(--text-xs)', fontWeight: 500 }}>Type</span>
                  <span style={{ color: 'var(--text-primary)', fontSize: 'var(--text-sm)', fontWeight: 500 }}>{currentLead.linkedin_company_type}</span>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* ─── RIGHT: AI Insights ─── */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-xl)' }}>

          <div style={{
            background: 'var(--bg-secondary)', border: '1px solid var(--border-primary)',
            borderRadius: 'var(--radius-xl)', padding: '20px 24px',
            boxShadow: 'var(--shadow-sm)',
            borderLeft: '3px solid var(--accent-amber)',
          }}>
            <h4 style={{
              display: 'flex', alignItems: 'center', gap: 8,
              color: 'var(--accent-amber)', fontSize: 'var(--text-xs)', fontWeight: 500,
              textTransform: 'uppercase', letterSpacing: '0.05em',
              margin: '0 0 16px', paddingBottom: 12, borderBottom: '1px solid var(--border-primary)',
            }}>
              <Brain size={16} /> AI Insights
            </h4>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>

                <>
                  {currentLead.pain_points && (
                    <div style={{ padding: '12px 16px', borderRadius: 'var(--radius-md)', background: 'var(--error-bg)', border: '1px solid rgba(239,68,68,0.2)' }}>
                      <p style={{ color: 'var(--error)', fontSize: 'var(--text-xs)', fontWeight: 500, margin: '0 0 4px', display: 'flex', alignItems: 'center', gap: 4 }}><AlertCircle size={14} /> Pain Points</p>
                      <p style={{ color: 'var(--text-primary)', fontSize: 'var(--text-sm)', margin: 0, lineHeight: 1.6 }}>{currentLead.pain_points}</p>
                    </div>
                  )}

                  {currentLead.revenue_leakage && (
                    <div style={{ padding: '12px 16px', borderRadius: 'var(--radius-md)', background: 'var(--warning-bg)', border: '1px solid rgba(245,158,11,0.2)' }}>
                      <p style={{ color: 'var(--warning)', fontSize: 'var(--text-xs)', fontWeight: 500, margin: '0 0 4px', display: 'flex', alignItems: 'center', gap: 4 }}>
                        <DollarSign size={14} /> Revenue Leakage
                      </p>
                      {(plan === 'free' || plan === 'starter' || plan === 'growth') ? (
                        <div style={{ textAlign: 'center', padding: '16px 0 4px' }}>
                          <div style={{ width: 36, height: 36, borderRadius: '50%', background: 'var(--warning-bg)', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 10px' }}>
                            <Lock size={18} color="var(--warning)" />
                          </div>
                          <p style={{ color: 'var(--text-secondary)', fontSize: 12, margin: '0 0 2px' }}>
                            Uncover where money is escaping this business.
                          </p>
                          <p style={{ color: 'var(--text-tertiary)', fontSize: 11, margin: '0 0 12px' }}>
                            Get specific dollar-impact analysis tied to their actual review data and operational gaps.
                          </p>
                          <a href="/dashboard/billing?tab=plans" style={{
                            display: 'inline-flex', alignItems: 'center', gap: 6,
                            padding: '6px 16px', borderRadius: 6,
                            className: 'subtle-upgrade-btn',
                            textDecoration: 'none', fontSize: 12, fontWeight: 500,
                          }}>
                            Upgrade to Pro <ArrowRight size={12} />
                          </a>
                        </div>
                      ) : (
                        <>
                          <p style={{ color: 'var(--text-primary)', fontSize: 'var(--text-sm)', margin: 0, lineHeight: 1.6, fontWeight: 500 }}>{currentLead.revenue_leakage}</p>
                          {currentLead.revenue_leakage_explanation && (
                            <p style={{ color: 'var(--text-secondary)', fontSize: 'var(--text-xs)', marginTop: 4, fontStyle: 'italic' }}>
                              {currentLead.revenue_leakage_explanation}
                            </p>
                          )}
                        </>
                      )}
                    </div>
                  )}

                  {currentLead.roi_potential && (
                    <div style={{ padding: '12px 16px', borderRadius: 'var(--radius-md)', background: 'var(--success-bg)', border: '1px solid rgba(16,185,129,0.2)' }}>
                      <p style={{ color: 'var(--success)', fontSize: 'var(--text-xs)', fontWeight: 500, margin: '0 0 4px', display: 'flex', alignItems: 'center', gap: 4 }}><TrendingUp size={14} /> ROI Potential</p>
                      {(plan === 'free' || plan === 'starter') ? (
                        <div style={{ textAlign: 'center', padding: '16px 0 4px' }}>
                          <div style={{ width: 36, height: 36, borderRadius: '50%', background: 'var(--warning-bg)', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 10px' }}>
                            <Lock size={18} color="var(--warning)" />
                          </div>
                          <p style={{ color: 'var(--text-secondary)', fontSize: 12, margin: '0 0 2px' }}>
                            See the return on investment this lead can deliver.
                          </p>
                          <p style={{ color: 'var(--text-tertiary)', fontSize: 11, margin: '0 0 12px' }}>
                            Get ROI estimates with specific math showing how the service pays for itself.
                          </p>
                          <a href="/dashboard/billing?tab=plans" style={{
                            display: 'inline-flex', alignItems: 'center', gap: 6,
                            padding: '6px 16px', borderRadius: 6,
                            className: 'subtle-upgrade-btn',
                            textDecoration: 'none', fontSize: 12, fontWeight: 500,
                          }}>
                            Upgrade to Growth <ArrowRight size={12} />
                          </a>
                        </div>
                      ) : (
                        <p style={{ color: 'var(--text-primary)', fontSize: 'var(--text-sm)', margin: 0, lineHeight: 1.6 }}>{currentLead.roi_potential}</p>
                      )}
                    </div>
                  )}

                  {currentLead.qualification_notes && (
                    <div style={{ padding: '12px 16px', borderRadius: 'var(--radius-md)', background: 'var(--info-bg)', border: '1px solid rgba(59,130,246,0.2)' }}>
                      <p style={{ color: 'var(--info)', fontSize: 'var(--text-xs)', fontWeight: 500, margin: '0 0 4px', display: 'flex', alignItems: 'center', gap: 4 }}>
                        <Award size={14} /> Qualification Notes
                      </p>
                      <p style={{ color: 'var(--text-primary)', fontSize: 'var(--text-sm)', margin: 0, lineHeight: 1.6 }}>{currentLead.qualification_notes}</p>
                    </div>
                  )}

                  {!currentLead.narrative && !currentLead.pain_points && !currentLead.revenue_leakage && !currentLead.roi_potential && !currentLead.qualification_notes && (
                    <p style={{ color: 'var(--text-tertiary)', fontSize: 'var(--text-sm)', textAlign: 'center', padding: '24px 0' }}>
                      No AI insights yet. Run Enrich & Score to generate.
                    </p>
                  )}
                </>
            </div>
          </div>

          {/* Actions */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            <button onClick={doBoth} disabled={isProcessing} style={{
              display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 6,
              height: 36, fontSize: 12, fontWeight: 500,
              padding: '0 16px', borderRadius: 'var(--radius-md)',
              border: '1px solid var(--border-primary)', background: 'transparent',
              color: isProcessing ? 'var(--text-tertiary)' : 'var(--text-secondary)',
              cursor: isProcessing ? 'not-allowed' : 'pointer',
              opacity: isProcessing ? 0.5 : 1,
              transition: 'all 0.2s ease',
              alignSelf: 'flex-start',
            }}>
              {reProcessing ? <LoadingDots /> : <><RefreshCw size={13} /> Enrich & Score</>}
            </button>
            {(role === 'admin' || role === 'super_admin') && (
            <div style={{ display: 'flex', gap: 8 }}>
              <button onClick={doEnrich} disabled={isProcessing} style={{
                flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6,
                padding: '10px 16px', borderRadius: 'var(--radius-md)',
                border: '1px solid var(--border-primary)', background: 'transparent',
                color: isProcessing ? 'var(--text-tertiary)' : 'var(--text-secondary)',
                cursor: isProcessing ? 'not-allowed' : 'pointer',
                fontSize: 'var(--text-xs)', fontWeight: 500,
                opacity: isProcessing ? 0.5 : 1,
                transition: 'all 0.2s ease',
              }}>
                {reEnriching ? <LoadingDots /> : <><RefreshCw size={14} /> Re-Enrich</>}
              </button>
              <button onClick={doScore} disabled={isProcessing} style={{
                flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6,
                padding: '10px 16px', borderRadius: 'var(--radius-md)',
                border: '1px solid var(--border-primary)', background: 'transparent',
                color: isProcessing ? 'var(--text-tertiary)' : 'var(--text-secondary)',
                cursor: isProcessing ? 'not-allowed' : 'pointer',
                fontSize: 'var(--text-xs)', fontWeight: 500,
                opacity: isProcessing ? 0.5 : 1,
                transition: 'all 0.2s ease',
              }}>
                {reScoring ? <LoadingDots /> : <><Brain size={14} /> Re-Score</>}
              </button>
            </div>
            )}
          </div>
        </div>
      </div>

      {/* ─── FULL-WIDTH SECTIONS: Reviews, Tech, Hours ─── */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-xl)', marginBottom: 'var(--space-3xl)' }}>

        {/* Reviews */}
        {(currentLead.google_review_sentiment || reviewDist) && (
          <div style={{
            background: 'var(--bg-secondary)', border: '1px solid var(--border-primary)',
            borderRadius: 'var(--radius-xl)', padding: '20px 24px',
            boxShadow: 'var(--shadow-sm)',
          }}>
            <h4 style={{
              display: 'flex', alignItems: 'center', gap: 8,
              color: 'var(--text-secondary)', fontSize: 'var(--text-xs)', fontWeight: 500,
              textTransform: 'uppercase', letterSpacing: '0.05em',
              margin: '0 0 16px', paddingBottom: 12, borderBottom: '1px solid var(--border-primary)',
            }}>
              <Star size={16} /> Reviews & Sentiment
            </h4>
            {currentLead.google_review_sentiment && (
              <span style={{
                display: 'inline-block', padding: '4px 16px', borderRadius: 'var(--radius-full)',
                fontSize: 'var(--text-xs)', fontWeight: 500, marginBottom: 16,
                background: currentLead.google_review_sentiment.toLowerCase().includes('positive') ? 'var(--success-bg)' : currentLead.google_review_sentiment.toLowerCase().includes('negative') ? 'var(--error-bg)' : 'var(--warning-bg)',
                color: currentLead.google_review_sentiment.toLowerCase().includes('positive') ? 'var(--success)' : currentLead.google_review_sentiment.toLowerCase().includes('negative') ? 'var(--error)' : 'var(--warning)',
              }}>
                {currentLead.google_review_sentiment}
              </span>
            )}
            {(plan === 'pro' || plan === 'enterprise') && currentLead.sentiment_positive_pct != null && (
              <div style={{ display: 'flex', gap: 12, marginBottom: 16 }}>
                <div style={{ flex: 1, textAlign: 'center', padding: '8px', borderRadius: 'var(--radius-md)', background: 'var(--success-bg)' }}>
                  <div style={{ fontSize: 'var(--text-lg)', fontWeight: 700, color: 'var(--success)' }}>{currentLead.sentiment_positive_pct || 0}%</div>
                  <div style={{ fontSize: 10, color: 'var(--text-tertiary)', marginTop: 2 }}>Positive</div>
                </div>
                <div style={{ flex: 1, textAlign: 'center', padding: '8px', borderRadius: 'var(--radius-md)', background: 'var(--warning-bg)' }}>
                  <div style={{ fontSize: 'var(--text-lg)', fontWeight: 700, color: 'var(--warning)' }}>{currentLead.sentiment_neutral_pct || 0}%</div>
                  <div style={{ fontSize: 10, color: 'var(--text-tertiary)', marginTop: 2 }}>Neutral</div>
                </div>
                <div style={{ flex: 1, textAlign: 'center', padding: '8px', borderRadius: 'var(--radius-md)', background: 'var(--error-bg)' }}>
                  <div style={{ fontSize: 'var(--text-lg)', fontWeight: 700, color: 'var(--error)' }}>{currentLead.sentiment_negative_pct || 0}%</div>
                  <div style={{ fontSize: 10, color: 'var(--text-tertiary)', marginTop: 2 }}>Negative</div>
                </div>
              </div>
            )}
            {reviewDist && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                {[5, 4, 3, 2, 1].map(stars => {
                  const count = reviewDist[stars] || 0;
                  const pct = (count / maxReview) * 100;
                  return (
                    <div key={stars} style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                      <span style={{ color: 'var(--text-secondary)', fontSize: 'var(--text-xs)', width: 60, textAlign: 'right' }}>
                        {'★'.repeat(stars)}{'☆'.repeat(5 - stars)}
                      </span>
                      <div style={{ flex: 1, height: 8, background: 'var(--border-primary)', borderRadius: 4, overflow: 'hidden' }}>
                        <div style={{ width: `${pct}%`, height: '100%', background: 'linear-gradient(90deg, #f59e0b, #fbbf24)', borderRadius: 4, transition: 'width 1s ease' }} />
                      </div>
                      <span style={{ color: 'var(--text-tertiary)', fontSize: 'var(--text-xs)', width: 36, flexShrink: 0 }}>{count}</span>
                    </div>
                  );
                })}
              </div>
            )}
            {currentLead.top_complaints && currentLead.top_complaints !== 'None' && plan === 'growth' && (
              <div style={{ marginTop: 16 }}>
                <p style={{ color: 'var(--text-tertiary)', fontSize: 10, fontWeight: 500, textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 6 }}>Top Complaints</p>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                  {currentLead.top_complaints.split(',').map((c, i) => {
                    const t = c.trim();
                    return t ? <span key={i} style={{ padding: '3px 10px', borderRadius: 'var(--radius-full)', background: 'var(--error-bg)', color: 'var(--error)', fontSize: 'var(--text-xs)', fontWeight: 500, border: '1px solid rgba(239,68,68,0.15)' }}>{t}</span> : null;
                  })}
                </div>
              </div>
            )}
            {(plan === 'pro' || plan === 'enterprise') && currentLead.complaint_categories && (() => {
              try {
                const cats = typeof currentLead.complaint_categories === 'string' ? JSON.parse(currentLead.complaint_categories) : currentLead.complaint_categories;
                if (cats && Object.keys(cats).length > 0) {
                  return (
                    <div style={{ marginTop: 16 }}>
                      <p style={{ color: 'var(--text-tertiary)', fontSize: 10, fontWeight: 500, textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 6 }}>Complaint Categories</p>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                        {Object.entries(cats).map(([cat, items]) => (
                          <div key={cat} style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                            <span style={{ fontSize: 'var(--text-xs)', color: 'var(--text-secondary)', minWidth: 120 }}>{cat}</span>
                            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
                              {(Array.isArray(items) ? items : [items]).map((item, i) => (
                                <span key={i} style={{ padding: '2px 8px', borderRadius: 'var(--radius-full)', background: 'var(--error-bg)', color: 'var(--error)', fontSize: 10 }}>{item}</span>
                              ))}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  );
                }
              } catch {}
              return null;
            })()}
            {currentLead.rating_trend && (
              <div style={{ marginTop: 12, display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                <span style={{ color: 'var(--text-secondary)', fontSize: 'var(--text-xs)' }}>
                  <TrendingUp size={12} style={{ marginRight: 4 }} /> Rating is {currentLead.rating_trend}
                </span>
                {(plan === 'pro' || plan === 'enterprise') && currentLead.trend_direction != null && currentLead.trend_direction !== 0 && (
                  <span style={{
                    fontSize: 10, fontWeight: 500,
                    color: currentLead.trend_direction > 0 ? 'var(--success)' : 'var(--error)',
                    background: (currentLead.trend_direction > 0 ? 'var(--success-bg)' : 'var(--error-bg)'),
                    padding: '2px 8px', borderRadius: 'var(--radius-full)',
                  }}>
                    {currentLead.trend_direction > 0 ? '+' : ''}{currentLead.trend_direction} pts
                  </span>
                )}
              </div>
            )}
            {plan === 'enterprise' && currentLead.trend_forecast && currentLead.trend_forecast !== 'Insufficient data for forecasting' && (
              <div style={{ marginTop: 12, padding: '8px 14px', borderRadius: 'var(--radius-md)', background: 'var(--accent-amber)10', border: '1px solid var(--accent-amber)30' }}>
                <p style={{ color: 'var(--accent-amber)', fontSize: 'var(--text-xs)', fontWeight: 500, margin: 0, display: 'flex', alignItems: 'center', gap: 6 }}>
                  <Sparkles size={12} /> {currentLead.trend_forecast}
                </p>
              </div>
            )}
          </div>
        )}

        {/* ─── ADVANCED FORECASTING (Growth+) ─── */}
        {currentLead.market_position && currentLead.market_position !== 'Unknown' && currentLead.market_position !== '' && (
          <div style={{
            background: 'var(--bg-secondary)', border: '1px solid var(--border-primary)',
            borderRadius: 'var(--radius-xl)', overflow: 'hidden',
            boxShadow: 'var(--shadow-sm)', marginBottom: 20,
          }}>
            {/* ── HEADER ── */}
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '14px 20px', borderBottom: '1px solid var(--border-primary)', background: 'var(--bg-tertiary)' }}>
              <h4 style={{ display: 'flex', alignItems: 'center', gap: 8, color: 'var(--text-secondary)', fontSize: 11, fontWeight: 500, textTransform: 'uppercase', letterSpacing: '0.06em', margin: 0 }}>
                <TrendingUp size={14} /> Market Position & Forecast
              </h4>
              {(plan === 'pro' || plan === 'enterprise') && (
                <span style={{ padding: '2px 10px', borderRadius: 'var(--radius-full)', fontSize: 10, fontWeight: 500, background: 'var(--accent-glow)', color: 'var(--accent-primary)', border: '1px solid rgba(37,99,235,0.2)' }}>
                  {plan.toUpperCase()}
                </span>
              )}
            </div>

            <div style={{ padding: '20px' }}>
              {plan === 'free' || plan === 'starter' || plan === 'growth' ? (
                <div style={{ textAlign: 'center', padding: '24px 16px' }}>
                  <div style={{ width: 48, height: 48, borderRadius: '50%', background: 'var(--warning-bg)', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 12px' }}>
                    <Lock size={22} color="var(--warning)" />
                  </div>
                  <p style={{ color: 'var(--text-secondary)', fontSize: 13, margin: '0 0 4px', lineHeight: 1.6 }}>
                    See where this business stands in the market.
                  </p>
                  <p style={{ color: 'var(--text-tertiary)', fontSize: 12, margin: '0 0 16px' }}>
                    Get market position analysis, competitor benchmarks, rating gauge, sentiment trajectory, revenue impact forecasts, and 3/6-month rating projections.
                  </p>
                  <a href="/dashboard/billing?tab=plans" style={{
                    display: 'inline-flex', alignItems: 'center', gap: 6,
                    padding: '8px 20px', borderRadius: 8,
                    background: 'transparent', border: '1px solid var(--border-primary)', color: 'var(--text-secondary)',
                    textDecoration: 'none', fontSize: 13, fontWeight: 500,
                  }}>
                    Upgrade to Pro <ArrowRight size={14} />
                  </a>
                </div>
              ) : (
                <>  
                {/* ═══ TIER 1: Instant Visual Scan — Rating Gauge + Position Badge ═══ */}
                <div style={{ marginBottom: 18 }}>
                  {/* Rating Gauge */}
                  <div style={{ padding: '24px 18px 16px', borderRadius: 'var(--radius-lg)', background: 'var(--bg-tertiary)', border: '1px solid var(--border-primary)', marginBottom: 10, overflow: 'visible' }}>
                    <div style={{ position: 'relative', display: 'flex', justifyContent: 'center', minHeight: 130 }}>
                      <svg width="260" height="130" viewBox="0 0 260 130" style={{ overflow: 'visible' }}>
                        <defs>
                          <linearGradient id="gaugeGradient" x1="0%" y1="0%" x2="100%" y2="0%">
                            <stop offset="0%" stopColor="#ef4444" />
                            <stop offset="30%" stopColor="#f59e0b" />
                            <stop offset="60%" stopColor="#22c55e" />
                            <stop offset="100%" stopColor="#10b981" />
                          </linearGradient>
                        </defs>
                        <path d="M 30 110 A 90 90 0 0 1 230 110" fill="none" stroke="var(--border-primary)" strokeWidth="14" strokeLinecap="round" />
                        <path d="M 30 110 A 90 90 0 0 1 230 110" fill="none" stroke="url(#gaugeGradient)" strokeWidth="14" strokeLinecap="round" 
                          strokeDasharray={`${(currentLead.google_rating || 0) / 5 * 314} 314`} />
                        <circle cx={30 + ((currentLead.google_rating || 0) / 5 * 200)} cy={110 - Math.sin(((currentLead.google_rating || 0) / 5) * Math.PI) * 90} r="7" fill="var(--text-primary)" stroke="var(--bg-secondary)" strokeWidth="3" />
                      </svg>
                      <div style={{ position: 'absolute', bottom: 8, left: '50%', transform: 'translateX(-50%)', textAlign: 'center' }}>
                        <div style={{ fontSize: 32, fontWeight: 800, color: 'var(--text-primary)', lineHeight: 1, letterSpacing: '-0.5px' }}>{currentLead.google_rating}★</div>
                        <div style={{ fontSize: 11, color: 'var(--text-tertiary)', marginTop: 2 }}>
                          {currentLead.google_review_count} reviews
                          {currentLead.trend_direction != null && currentLead.trend_direction !== 0 && (
                            <span style={{ marginLeft: 6, fontWeight: 500, color: currentLead.trend_direction > 0 ? 'var(--success)' : 'var(--error)' }}>
                              {currentLead.trend_direction > 0 ? '↗' : '↘'} {Math.abs(currentLead.trend_direction)}pts
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 10, color: 'var(--text-tertiary)', padding: '0 22px', marginTop: 4 }}>
                      <span style={{ color: '#ef4444' }}>1</span><span>2</span><span>3</span><span>4</span><span style={{ color: '#10b981' }}>5</span>
                    </div>
                  </div>

                  {/* Position Badge + One-line Analysis */}
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 16px', borderRadius: 'var(--radius-lg)',
                    background: currentLead.market_position === 'Leader' ? 'rgba(34,197,94,0.06)' : 
                                currentLead.market_position === 'Strong' ? 'rgba(59,130,246,0.06)' : 
                                currentLead.market_position === 'At Risk' ? 'rgba(239,68,68,0.06)' : 
                                'rgba(245,158,11,0.06)',
                    border: `1px solid ${currentLead.market_position === 'Leader' ? 'rgba(34,197,94,0.15)' : currentLead.market_position === 'Strong' ? 'rgba(59,130,246,0.15)' : currentLead.market_position === 'At Risk' ? 'rgba(239,68,68,0.15)' : 'rgba(245,158,11,0.15)'}` }}>
                    <span style={{ padding: '3px 10px', borderRadius: 'var(--radius-full)', fontSize: 12, fontWeight: 700,
                      color: currentLead.market_position === 'Leader' ? 'var(--success)' : currentLead.market_position === 'Strong' ? 'var(--info)' : currentLead.market_position === 'At Risk' ? 'var(--error)' : 'var(--warning)',
                      background: currentLead.market_position === 'Leader' ? 'rgba(34,197,94,0.15)' : currentLead.market_position === 'Strong' ? 'rgba(59,130,246,0.15)' : currentLead.market_position === 'At Risk' ? 'rgba(239,68,68,0.15)' : 'rgba(245,158,11,0.15)',
                    }}>
                      {currentLead.market_position === 'Leader' ? '🏆' : currentLead.market_position === 'Strong' ? '📈' : currentLead.market_position === 'At Risk' ? '⚠️' : '🆘'} {currentLead.market_position}
                    </span>
                    <span style={{ fontSize: 12, color: 'var(--text-secondary)', lineHeight: 1.5, flex: 1 }}>
                      {currentLead.market_position_analysis || currentLead.competitor_context || currentLead.competitor_benchmark || ''}
                    </span>
                  </div>
                </div>

                {/* ═══ TIER 2: Sentiment Bar ═══ */}
                {currentLead.sentiment_positive_pct != null && (
                  <div style={{ marginBottom: 16 }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4, fontSize: 11 }}>
                      <span style={{ fontWeight: 500, color: 'var(--success)' }}>{currentLead.sentiment_positive_pct}% Positive</span>
                      <span style={{ fontWeight: 500, color: 'var(--warning)' }}>{currentLead.sentiment_neutral_pct}% Neutral</span>
                      <span style={{ fontWeight: 500, color: 'var(--error)' }}>{currentLead.sentiment_negative_pct}% Negative</span>
                    </div>
                    <div style={{ height: 7, borderRadius: 4, overflow: 'hidden', background: 'var(--bg-primary)', display: 'flex' }}>
                      <div style={{ width: `${currentLead.sentiment_positive_pct}%`, background: 'var(--success)' }} />
                      <div style={{ width: `${currentLead.sentiment_neutral_pct}%`, background: 'var(--warning)' }} />
                      <div style={{ width: `${currentLead.sentiment_negative_pct}%`, background: 'var(--error)', animation: currentLead.sentiment_negative_pct > 15 ? 'pulse 2s infinite' : 'none' }} />
                    </div>
                  </div>
                )}

                {/* ═══ TIER 3: Supporting Metrics ═══ */}
                {(plan === 'pro' || plan === 'enterprise') && (
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 16 }}>
                    <span style={{ padding: '4px 10px', borderRadius: 'var(--radius-full)', fontSize: 11, fontWeight: 500, background: 'var(--bg-tertiary)', color: 'var(--text-secondary)', border: '1px solid var(--border-primary)' }}>
                      {currentLead.review_velocity_trend || 'Steady'}{currentLead.review_velocity > 0 ? ` · ${currentLead.review_velocity}/mo` : ''}
                    </span>
                    <span style={{ padding: '4px 10px', borderRadius: 'var(--radius-full)', fontSize: 11, fontWeight: 500,
                      background: currentLead.rating_volatility === 'High' ? 'rgba(239,68,68,0.08)' : currentLead.rating_volatility === 'Stable' ? 'rgba(34,197,94,0.08)' : 'var(--bg-tertiary)',
                      color: currentLead.rating_volatility === 'High' ? 'var(--error)' : currentLead.rating_volatility === 'Stable' ? 'var(--success)' : 'var(--text-secondary)',
                      border: `1px solid ${currentLead.rating_volatility === 'High' ? 'rgba(239,68,68,0.2)' : currentLead.rating_volatility === 'Stable' ? 'rgba(34,197,94,0.2)' : 'var(--border-primary)'}` }}>
                      {currentLead.rating_volatility === 'High' ? 'High volatility' : currentLead.rating_volatility === 'Stable' ? 'Stable rating' : currentLead.rating_volatility || 'N/A'}
                    </span>
                    <span style={{ padding: '4px 10px', borderRadius: 'var(--radius-full)', fontSize: 11, fontWeight: 500,
                      background: currentLead.rating_trend === 'Declining' ? 'rgba(239,68,68,0.08)' : currentLead.rating_trend === 'Improving' ? 'rgba(34,197,94,0.08)' : 'var(--bg-tertiary)',
                      color: currentLead.rating_trend === 'Declining' ? 'var(--error)' : currentLead.rating_trend === 'Improving' ? 'var(--success)' : 'var(--text-secondary)',
                      border: `1px solid ${currentLead.rating_trend === 'Declining' ? 'rgba(239,68,68,0.2)' : currentLead.rating_trend === 'Improving' ? 'rgba(34,197,94,0.2)' : 'var(--border-primary)'}` }}>
                      {currentLead.rating_trend === 'Declining' ? 'Declining' : currentLead.rating_trend === 'Improving' ? 'Improving' : currentLead.rating_trend === 'Stable' ? 'Stable' : currentLead.rating_trend || 'N/A'}
                      {currentLead.trend_direction != null && currentLead.trend_direction !== 0 && ` · ${currentLead.trend_direction > 0 ? '+' : ''}${currentLead.trend_direction}pts`}
                    </span>
                    {currentLead.complaint_frequency && currentLead.complaint_frequency !== 'Unknown' && (
                      <span style={{ padding: '4px 10px', borderRadius: 'var(--radius-full)', fontSize: 11, fontWeight: 500,
                        background: currentLead.complaint_frequency.includes('Systemic') ? 'rgba(239,68,68,0.08)' : currentLead.complaint_frequency.includes('Rare') ? 'rgba(34,197,94,0.08)' : 'var(--bg-tertiary)',
                        color: currentLead.complaint_frequency.includes('Systemic') ? 'var(--error)' : currentLead.complaint_frequency.includes('Rare') ? 'var(--success)' : 'var(--text-secondary)',
                        border: `1px solid ${currentLead.complaint_frequency.includes('Systemic') ? 'rgba(239,68,68,0.2)' : currentLead.complaint_frequency.includes('Rare') ? 'rgba(34,197,94,0.2)' : 'var(--border-primary)'}` }}>
                        {currentLead.complaint_frequency.includes('Systemic') ? 'Systemic complaints' : currentLead.complaint_frequency.includes('Rare') ? 'Rare complaints' : 'Moderate complaints'}
                      </span>
                    )}
                  </div>
                )}

                {/* ═══ TIER 4: Forward-Looking Intelligence ═══ */}
                {(currentLead.market_narrative || currentLead.competitor_context) && (
                  <div style={{ padding: '12px 14px', borderRadius: 'var(--radius-lg)', background: 'var(--bg-tertiary)', border: '1px solid var(--border-primary)', marginBottom: 12 }}>
                    <p style={{ fontSize: 12, color: 'var(--text-secondary)', margin: 0, lineHeight: 1.65 }}>
                      {currentLead.market_narrative || currentLead.competitor_context}
                    </p>
                  </div>
                )}

                {/* Trajectory Timeline */}
                {(plan === 'pro' || plan === 'enterprise') && (currentLead.sentiment_trajectory_detailed || currentLead.sentiment_trajectory) && (
                  <div style={{ marginBottom: 12 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 0, marginBottom: 6 }}>
                      <div style={{ width: 8, height: 8, borderRadius: '50%', background: 'var(--success)' }} />
                      <div style={{ flex: 1, height: 1.5, background: 'linear-gradient(90deg, var(--success), var(--text-tertiary))' }} />
                      <div style={{ width: 8, height: 8, borderRadius: '50%', background: 'var(--text-tertiary)' }} />
                      <div style={{ flex: 1, height: 1.5, borderTop: '1.5px dashed var(--border-primary)' }} />
                      <div style={{ width: 8, height: 8, borderRadius: '50%', border: '1.5px dashed var(--text-tertiary)' }} />
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 10, color: 'var(--text-tertiary)', marginBottom: 6 }}>
                      <span>Past</span><span>Now</span><span>Projected</span>
                    </div>
                    <p style={{ fontSize: 12, color: 'var(--text-secondary)', margin: 0, lineHeight: 1.6, textAlign: 'center' }}>
                      {currentLead.sentiment_trajectory_detailed || currentLead.sentiment_trajectory}
                    </p>
                  </div>
                )}

                {/* Forecast Numbers */}
                {(currentLead.rating_forecast_3m || currentLead.rating_forecast_6m) && (
                  <div style={{ display: 'flex', gap: 10, marginBottom: 12 }}>
                    {currentLead.rating_forecast_3m && (
                      <div style={{ flex: 1, padding: '10px', borderRadius: 'var(--radius-md)', background: 'var(--bg-tertiary)', border: '1px solid var(--border-primary)', textAlign: 'center' }}>
                        <div style={{ fontSize: 10, color: 'var(--text-tertiary)', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 2 }}>3-Month Forecast</div>
                        <div style={{ fontSize: 18, fontWeight: 700, color: 'var(--text-primary)' }}>{currentLead.rating_forecast_3m}★</div>
                      </div>
                    )}
                    {currentLead.rating_forecast_6m && (
                      <div style={{ flex: 1, padding: '10px', borderRadius: 'var(--radius-md)', background: 'var(--bg-tertiary)', border: '1px solid var(--border-primary)', textAlign: 'center' }}>
                        <div style={{ fontSize: 10, color: 'var(--text-tertiary)', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 2 }}>6-Month Forecast</div>
                        <div style={{ fontSize: 18, fontWeight: 700, color: 'var(--text-primary)' }}>{currentLead.rating_forecast_6m}★</div>
                      </div>
                    )}
                  </div>
                )}

                {/* ═══ TIER 5: Revenue Impact — The "So What" ═══ */}
                {(currentLead.roi_impact_detailed || currentLead.roi_impact_rating) && (
                  <div style={{ padding: '14px 16px', borderRadius: 'var(--radius-lg)', background: 'linear-gradient(135deg, rgba(245,158,11,0.06), rgba(245,158,11,0.01))', border: '1px solid rgba(245,158,11,0.15)' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 4 }}>
                      <DollarSign size={14} color='#f59e0b' />
                      <span style={{ fontSize: 10, fontWeight: 700, color: '#f59e0b', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Revenue Impact</span>
                    </div>
                    <p style={{ fontSize: 12, color: 'var(--text-primary)', margin: 0, lineHeight: 1.6, fontWeight: 500 }}>
                      {currentLead.roi_impact_detailed || currentLead.roi_impact_rating}
                    </p>
                  </div>
                )}
                </>
              )}
            </div>
          </div>
        )}

        {/* ─── TECHNOLOGY STACK ─── Enhanced Design ─── */}
        {
          <div style={{
            background: 'var(--bg-secondary)',
            border: '1px solid var(--border-primary)',
            borderRadius: 'var(--radius-xl)',
            padding: '24px',
            boxShadow: 'var(--shadow-sm)',
          }}>
            {/* Header */}
            <div style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              marginBottom: 20,
              paddingBottom: 12,
              borderBottom: '1px solid var(--border-primary)',
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <Cpu size={18} color="var(--accent-primary)" />
                <h4 style={{
                  margin: 0,
                  color: 'var(--text-primary)',
                  fontSize: 'var(--text-base)',
                  fontWeight: 500,
                  letterSpacing: '-0.2px',
                }}>Technology Stack</h4>
              </div>
              {Object.keys(techStack).length > 0 && (
                <span style={{
                  fontSize: 'var(--text-xs)',
                  color: 'var(--text-tertiary)',
                  background: 'var(--bg-tertiary)',
                  padding: '2px 10px',
                  borderRadius: 'var(--radius-full)',
                  border: '1px solid var(--border-primary)',
                }}>
                  {Object.values(techStack).reduce((acc, arr) => acc + arr.length, 0)} technologies
                </span>
              )}
            </div>

            {/* Grid of categories */}
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))',
              gap: 16,
            }}>
              {Object.entries(techStack).map(([categoryKey, techs]) => {
                const meta = TECH_LABELS[categoryKey];
                if (!meta) return null;
                const color = meta.color;
                const bg = `${color}10`;

                return (
                  <div key={categoryKey} style={{
                    background: 'var(--bg-tertiary)',
                    borderRadius: 'var(--radius-lg)',
                    padding: '14px 16px',
                    border: '1px solid var(--border-primary)',
                    transition: 'all 0.25s cubic-bezier(0.4, 0, 0.2, 1)',
                    cursor: 'default',
                  }}
                  onMouseEnter={e => {
                    e.currentTarget.style.borderColor = color + '50';
                    e.currentTarget.style.transform = 'translateY(-3px)';
                    e.currentTarget.style.boxShadow = 'var(--shadow-md)';
                  }}
                  onMouseLeave={e => {
                    e.currentTarget.style.borderColor = 'var(--border-primary)';
                    e.currentTarget.style.transform = 'translateY(0)';
                    e.currentTarget.style.boxShadow = 'none';
                  }}
                  >
                    {/* Category header */}
                    <div style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      marginBottom: 10,
                    }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <span style={{
                          display: 'inline-block',
                          width: 10,
                          height: 10,
                          borderRadius: '50%',
                          background: color,
                          boxShadow: `0 0 12px ${color}40`,
                          flexShrink: 0,
                        }} />
                        <span style={{
                          color: 'var(--text-primary)',
                          fontSize: 'var(--text-sm)',
                          fontWeight: 500,
                        }}>{meta.label}</span>
                      </div>
                      <span style={{
                        fontSize: 10,
                        fontWeight: 500,
                        color: color,
                        background: bg,
                        padding: '1px 8px',
                        borderRadius: 'var(--radius-full)',
                        border: `1px solid ${color}25`,
                      }}>{techs.length}</span>
                    </div>

                    {/* Technology badges */}
                    <div style={{
                      display: 'flex',
                      flexWrap: 'wrap',
                      gap: 6,
                    }}>
                      {techs.slice(0, 4).map((tech) => {
                        const logoKey = tech.toLowerCase().replace(/[^a-z0-9_]/g, '_');
                        const logoSrc = TECH_LOGOS[logoKey];
                        return (
                          <span key={tech} style={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: 4,
                            padding: logoSrc ? '2px 8px 2px 4px' : '3px 8px',
                            borderRadius: 'var(--radius-sm)',
                            background: `color-mix(in srgb, ${color} 12%, var(--bg-secondary))`,
                            color: color,
                            fontSize: 11,
                            fontWeight: 500,
                            border: `1px solid ${color}25`,
                            transition: 'all 0.15s ease',
                          }}
                          onMouseEnter={e => {
                            e.currentTarget.style.background = `color-mix(in srgb, ${color} 25%, var(--bg-secondary))`;
                            e.currentTarget.style.transform = 'scale(1.03)';
                          }}
                          onMouseLeave={e => {
                            e.currentTarget.style.background = `color-mix(in srgb, ${color} 12%, var(--bg-secondary))`;
                            e.currentTarget.style.transform = 'scale(1)';
                          }}
                          >
                            {logoSrc && (
                              <img
                                src={logoSrc}
                                alt=""
                                style={{ width: 14, height: 14, flexShrink: 0 }}
                                onError={e => e.target.style.display = 'none'}
                              />
                            )}
                            {tech}
                          </span>
                        );
                      })}
                      {techs.length > 4 && (
                        <span style={{
                          padding: '2px 8px',
                          borderRadius: 'var(--radius-sm)',
                          background: 'var(--bg-secondary)',
                          color: 'var(--text-tertiary)',
                          fontSize: 10,
                          fontWeight: 500,
                          border: '1px solid var(--border-primary)',
                        }}>
                          +{techs.length - 4}
                        </span>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
            {Object.keys(techStack).length === 0 && (
              <div style={{
                textAlign: 'center',
                padding: '24px 16px',
                color: 'var(--text-tertiary)',
                fontSize: 'var(--text-sm)',
                background: 'var(--bg-tertiary)',
                borderRadius: 'var(--radius-lg)',
                border: '1px dashed var(--border-primary)',
              }}>
                No technology stack detected for this lead.
              </div>
            )}
          </div>
        }

        {/* ─── Opening Hours ─── */}
        <div style={{
          background: 'var(--bg-secondary)', border: '1px solid var(--border-primary)',
          borderRadius: 'var(--radius-xl)', padding: '20px 24px',
          boxShadow: 'var(--shadow-sm)',
        }}>
          <h4 style={{
            display: 'flex', alignItems: 'center', gap: 8,
            color: 'var(--text-secondary)', fontSize: 'var(--text-xs)', fontWeight: 500,
            textTransform: 'uppercase', letterSpacing: '0.05em',
            margin: '0 0 16px', paddingBottom: 12, borderBottom: '1px solid var(--border-primary)',
          }}>
            <Clock size={16} /> Opening Hours
          </h4>
          {hoursData && Object.keys(hoursData).length > 0 ? (
            <div style={{ borderRadius: 'var(--radius-md)', overflow: 'hidden', border: '1px solid var(--border-primary)' }}>
              {DAYS.filter(d => hoursData[d]).map((day, i, arr) => (
                <div key={day} style={{
                  display: 'flex', justifyContent: 'space-between',
                  padding: '8px 16px',
                  background: i % 2 === 0 ? 'var(--bg-tertiary)' : 'transparent',
                  borderBottom: i < arr.length - 1 ? '1px solid var(--border-primary)' : 'none',
                  fontSize: 'var(--text-sm)',
                }}>
                  <span style={{ color: 'var(--text-primary)', fontWeight: 500 }}>{day}</span>
                  <span style={{ color: 'var(--text-secondary)' }}>{hoursData[day]}</span>
                </div>
              ))}
            </div>
          ) : (
            <p style={{ color: 'var(--success)', fontSize: 'var(--text-sm)', fontWeight: 500, textAlign: 'center', padding: '24px 0' }}>
              🕐 Open 24 Hours
            </p>
          )}
        </div>
      </div>
    </div>
  );
}